<template>
  <!-- fullstory: fs-mask the whole ticket — description, comments, internal notes and
       contact pii are all unbounded customer free text. interactions / structure still
       record; only text content is blanked. -->
  <div class="std-root fs-mask">
    <!-- skeleton placeholder while load_data runs. shape mirrors the
         actual ticket: header (back + title + Agent + Edit, kebab
         when applicable), an admin triage band (admin only) with
         Dev Ticket + Internal Notes side by side, subject banner
         with content rail, 4-col metadata grid covering all five
         metadata rows, then a headless Attachments section + the
         framed Comments section. content rails (steelblue inset +
         14px padding-left) match the real post-load layout so the
         page settles into final content with minimal x/y drift. -->
    <template v-if="isLoading">
      <div class="std-card mt-2">
        <div class="std-header">
          <button type="button" class="std-back-btn" @click="goBackToList" aria-label="Back to support tickets">
            <i class="fa fa-chevron-left" aria-hidden="true"></i>
          </button>
          <span class="skeleton skeleton-title"></span>
          <div class="std-header-actions std-skeleton-header-actions">
            <span class="skeleton skeleton-btn"></span>
            <span class="skeleton skeleton-btn skeleton-btn--narrow"></span>
          </div>
        </div>

        <!-- subject banner placeholder (unlabeled, prominent) sits as
             the page's first content block, matching the rendered
             layout where the subject lives above the table. wrapped in
             std-content-rail so the steelblue rail + 14px left padding
             appear during load too, matching the post-load banner
             position exactly. -->
        <div class="std-skeleton-subject-row">
          <div class="std-content-rail">
            <span class="skeleton skeleton-subject"></span>
          </div>
        </div>

        <!-- admin triage band placeholder, only shown to admins. sits
             between the subject banner and the metadata grid, mirroring
             the real form's admin row at the top of the table. cream +
             gold pipe panel with Dev Ticket (1 col) beside Internal
             Notes (3 cols), stacking on narrow viewports just like the
             real row. -->
        <div v-if="effectiveRole === 'admin'" class="std-skeleton-admin">
          <div class="std-skeleton-cell">
            <span class="skeleton skeleton-label"></span>
            <span class="skeleton skeleton-value skeleton-value--narrow"></span>
          </div>
          <div class="std-skeleton-cell std-skeleton-admin-notes">
            <span class="skeleton skeleton-label"></span>
            <span class="skeleton skeleton-block skeleton-block--short"></span>
          </div>
        </div>

        <div class="std-skeleton-grid">
          <!-- row 1: Account | Type | Created | Last Updated -->
          <div class="std-skeleton-cell">
            <span class="skeleton skeleton-label"></span>
            <span class="skeleton skeleton-value"></span>
          </div>
          <div class="std-skeleton-cell">
            <span class="skeleton skeleton-label"></span>
            <span class="skeleton skeleton-value skeleton-value--narrow"></span>
          </div>
          <div class="std-skeleton-cell">
            <span class="skeleton skeleton-label"></span>
            <span class="skeleton skeleton-value skeleton-value--narrow"></span>
          </div>
          <div class="std-skeleton-cell">
            <span class="skeleton skeleton-label"></span>
            <span class="skeleton skeleton-value skeleton-value--narrow"></span>
          </div>
          <!-- row 2: Status | Priority (tag pill) | Feature | Assigned -->
          <div class="std-skeleton-cell">
            <span class="skeleton skeleton-label"></span>
            <span class="skeleton skeleton-value skeleton-value--narrow"></span>
          </div>
          <div class="std-skeleton-cell">
            <span class="skeleton skeleton-label"></span>
            <span class="skeleton skeleton-tag"></span>
          </div>
          <div class="std-skeleton-cell">
            <span class="skeleton skeleton-label"></span>
            <span class="skeleton skeleton-value"></span>
          </div>
          <div class="std-skeleton-cell">
            <span class="skeleton skeleton-label"></span>
            <span class="skeleton skeleton-value"></span>
          </div>
          <!-- row 3: admins see Created By | Creator Role | Estimated
               Dev | Actual Dev. muggles see only Created By (full row),
               since the trailing three are admin-tooling fields. the
               admin trailing cells are commonly empty post-load, so
               their skeletons match the muted std-cell--empty shape
               (label + thin baseline rule) and the page barely settles
               when content paints. -->
          <div class="std-skeleton-cell" :class="{ 'std-skeleton-cell--full': effectiveRole !== 'admin' }">
            <span class="skeleton skeleton-label"></span>
            <div class="std-content-rail">
              <span class="skeleton skeleton-value skeleton-value--wide"></span>
            </div>
          </div>
          <div v-if="effectiveRole === 'admin'" class="std-skeleton-cell std-skeleton-cell--empty">
            <span class="skeleton skeleton-label"></span>
          </div>
          <div v-if="effectiveRole === 'admin'" class="std-skeleton-cell std-skeleton-cell--empty">
            <span class="skeleton skeleton-label"></span>
          </div>
          <div v-if="effectiveRole === 'admin'" class="std-skeleton-cell std-skeleton-cell--empty">
            <span class="skeleton skeleton-label"></span>
          </div>
          <!-- row 4: admins see Target Date | Additional Contacts (2 col)
               | CC Account Admins. non-admins see Additional Contacts (3 col)
               | CC Account Admins — Target Date is admin-only. -->
          <div v-if="effectiveRole === 'admin'" class="std-skeleton-cell std-skeleton-cell--empty">
            <span class="skeleton skeleton-label"></span>
          </div>
          <div class="std-skeleton-cell std-skeleton-cell--empty"
               :class="effectiveRole === 'admin' ? 'std-skeleton-cell--span2' : 'std-skeleton-cell--span3'">
            <span class="skeleton skeleton-label"></span>
          </div>
          <div class="std-skeleton-cell std-skeleton-cell--empty">
            <span class="skeleton skeleton-label"></span>
          </div>
          <!-- Description (label + rail-wrapped body block). same rail
               as Subject + Created By so the page reads as one threaded
               primary-content column during load. -->
          <div class="std-skeleton-cell std-skeleton-cell--full">
            <span class="skeleton skeleton-label"></span>
            <div class="std-content-rail">
              <span class="skeleton skeleton-block"></span>
            </div>
          </div>
        </div>
      </div>

      <!-- Attachments section placeholder — headless variant (matches
           read-only post-load: no "Attachments" header, no hairline,
           tighter top margin). just the outline Add Attachment(s)
           button + dim "no attachments yet" hint inline. -->
      <div class="std-section-card std-section-card--headless">
        <div class="std-skeleton-attachments-row">
          <span class="skeleton skeleton-btn"></span>
          <span class="skeleton skeleton-empty-hint"></span>
        </div>
      </div>

      <!-- Comments section card placeholder. shape mirrors the real
           section: title + subtitle, composer (label + textarea +
           attach/post buttons), day-group header, and one ghost
           comment card with puck + name row + body so paint-in
           doesn't visibly reflow. -->
      <div class="std-section-card">
        <div class="std-section-header">
          <div class="std-section-header-text">
            <span class="skeleton skeleton-section-title"></span>
            <span class="skeleton skeleton-subtitle"></span>
          </div>
        </div>
        <span class="skeleton skeleton-label"></span>
        <span class="skeleton skeleton-block skeleton-block--input"></span>
        <div class="std-skeleton-comment-row">
          <span class="skeleton skeleton-btn"></span>
          <span class="skeleton skeleton-btn skeleton-btn--narrow std-skeleton-post"></span>
        </div>
        <div class="std-skeleton-comment-card">
          <span class="skeleton skeleton-group-header"></span>
          <div class="std-skeleton-comment-item">
            <span class="skeleton skeleton-puck"></span>
            <div class="std-skeleton-comment-item-meta">
              <span class="skeleton skeleton-value skeleton-value--narrow"></span>
              <span class="skeleton skeleton-value skeleton-value--xnarrow"></span>
            </div>
          </div>
          <span class="skeleton skeleton-block skeleton-block--short"></span>
        </div>
      </div>
    </template>

    <!-- ticket-not-found / load-failed fallback. shown when load_data
         finished but no model was populated — was previously rendering
         the skeleton forever behind a generic "Error retrieving data"
         alert because the render threw on {{model.id}}. -->
    <div v-if="!isLoading && !model" class="std-card mt-2">
      <div class="std-header">
        <button type="button" class="std-back-btn" @click="goBackToList" aria-label="Back to support tickets">
          <i class="fa fa-chevron-left" aria-hidden="true"></i>
        </button>
        <h3 class="std-title" style="color: #6c757d;">Ticket not available</h3>
      </div>
      <div style="padding: 24px 8px; text-align: center; color: #6c757d;">
        <i class="fa fa-unlink" style="font-size: 28px; margin-bottom: 10px; display: block;"></i>
        <div style="font-size: 15px;">We couldn't load this support ticket.</div>
        <div style="font-size: 13px; margin-top: 6px;">
          It may have been deleted, or you may not have access to it. Try
          logging out and back in, or contact support if this keeps happening.
        </div>
        <b-button variant="outline-secondary" size="sm" class="mt-3" @click="goToSupportTicketList">
          <i class="fa fa-chevron-left" aria-hidden="true"></i>&nbsp;Back to support tickets
        </b-button>
      </div>
    </div>

    <div v-if="!isLoading && model" class="std-card mt-2">
      <div class="std-header">
        <button type="button" class="std-back-btn" @click="goBackToList" aria-label="Back to support tickets">
          <i class="fa fa-chevron-left" aria-hidden="true"></i>
        </button>
        <h3 class="std-title">Support Ticket #{{model.id}}</h3>
        <div class="std-header-actions">
          <!-- "Agent" export — sys-admin only, desktop-only. on mobile
               this collapses into the kebab below to free header space.
               server-side renders a markdown brief tailored to the
               ticket's tenant_type + category, copies it to the
               clipboard for handoff to an AI coding agent (Claude Code,
               Cursor, etc.). gold palette is the established admin-
               only triage cue (see help_center Article Leaderboard).
               sits to the left of Edit so the gold accent leads the
               action row instead of being sandwiched between the
               primary Edit and the kebab. -->
          <b-button
            v-if="effectiveRole === 'admin' && !isEditing && !isMobileViewport"
            size="sm"
            class="std-assist-btn"
            :class="{ 'std-assist-btn--copied': assistExported }"
            :disabled="assistExporting"
            @click="exportTicketForAssist"
            title="Copy an AI-ready ticket brief to the clipboard"
          >
            <!-- Claude splat — 8-pointed asterisk with tapered leaf
                 petals (4 long axis-aligned + 4 shorter diagonals).
                 paths instead of crossed ellipses so the petals have
                 pointy tapered tips like the real anthropic mark.
                 currentColor inverts cleanly against the filled
                 hover/copied states. spacing matches Edit's icon+nbsp
                 (no margin on the svg). -->
            <svg class="std-assist-splat" viewBox="3 3 18 18" aria-hidden="true"><g fill="currentColor"><path d="M 12 3 C 12.7 7 12.7 17 12 21 C 11.3 17 11.3 7 12 3 Z"/><path d="M 21 12 C 17 12.7 7 12.7 3 12 C 7 11.3 17 11.3 21 12 Z"/><g transform="rotate(45 12 12)"><path d="M 12 5.5 C 12.5 8 12.5 16 12 18.5 C 11.5 16 11.5 8 12 5.5 Z"/><path d="M 18.5 12 C 16 12.5 8 12.5 5.5 12 C 8 11.5 16 11.5 18.5 12 Z"/></g></g></svg>&nbsp;{{ assistExported ? 'Copied!' : (assistExporting ? 'Building…' : 'Agent') }}
          </b-button>
          <!-- Edit ↔ Discard swap on the same slot. "Discard" (not
               "Cancel Edit") avoids verb-collision with the destructive
               "Cancel Ticket" action tucked into the kebab. -->
          <b-button v-if="!isEditing && canEdit" size="sm" variant="outline-primary" @click="enterEditMode">
            <i class="fa fa-pencil" aria-hidden="true"></i>&nbsp;Edit
          </b-button>
          <b-button v-if="isEditing" size="sm" variant="outline-secondary" @click="cancelEditMode">
            <i class="fa fa-times" aria-hidden="true"></i>&nbsp;Discard
          </b-button>
          <!-- overflow menu for secondary / destructive / mobile-only
               actions. keeps "Cancel Ticket" out of the main action row
               so it can't be mis-clicked for "Discard" during an edit;
               on mobile also hosts the "Agent" export so the header
               stays compact. only renders when at least one item is
               applicable for the current viewport / role / status. -->
          <b-dropdown
            v-if="!isEditing && (canCancelTicket || (isMobileViewport && effectiveRole === 'admin'))"
            size="sm"
            variant="outline-secondary"
            right
            no-caret
            class="std-header-overflow"
            toggle-class="std-header-overflow-toggle"
            title="More actions"
          >
            <template slot="button-content">
              <i class="fa fa-ellipsis-v" aria-hidden="true"></i>
              <span class="sr-only">More actions</span>
            </template>
            <!-- mobile-only mirror of the standalone Agent button. same
                 splat icon + state machine; gold accent kept via the
                 std-assist-item class so the admin-only cue still
                 reads inside the menu. -->
            <b-dropdown-item
              v-if="isMobileViewport && effectiveRole === 'admin'"
              class="std-assist-item"
              :class="{ 'std-assist-item--copied': assistExported }"
              :disabled="assistExporting"
              @click="exportTicketForAssist"
              title="Copy an AI-ready ticket brief to the clipboard"
            >
              <svg class="std-assist-splat" viewBox="3 3 18 18" aria-hidden="true"><g fill="currentColor"><path d="M 12 3 C 12.7 7 12.7 17 12 21 C 11.3 17 11.3 7 12 3 Z"/><path d="M 21 12 C 17 12.7 7 12.7 3 12 C 7 11.3 17 11.3 21 12 Z"/><g transform="rotate(45 12 12)"><path d="M 12 5.5 C 12.5 8 12.5 16 12 18.5 C 11.5 16 11.5 8 12 5.5 Z"/><path d="M 18.5 12 C 16 12.5 8 12.5 5.5 12 C 8 11.5 16 11.5 18.5 12 Z"/></g></g></svg>&nbsp;{{ assistExported ? 'Copied!' : (assistExporting ? 'Building…' : 'Agent') }}
            </b-dropdown-item>
            <!-- divider only when both items are present, so a kebab
                 holding only Agent or only Cancel doesn't render a
                 stray separator. -->
            <b-dropdown-divider v-if="isMobileViewport && effectiveRole === 'admin' && canCancelTicket"/>
            <b-dropdown-item
              v-if="canCancelTicket"
              variant="danger"
              @click="confirmCancelTicket"
            >
              <i class="fa fa-ban" aria-hidden="true"></i>&nbsp;Cancel Ticket
            </b-dropdown-item>
          </b-dropdown>
        </div>
      </div>

      <form v-if="model" class="std-form" @dblclick="handleFormFieldDblClick">
        <!-- subject banner — primary content of the ticket, lifted out of
             the metadata grid so the title carries page-level weight. no
             label in view mode (the visual treatment is sufficient); the
             "Subject" label appears in edit mode to give the input an
             affordance. -->
        <div class="std-subject-banner std-content-rail" :class="{ 'std-subject-banner--editing': isEditing }">
          <h5 v-if="isEditing">Subject</h5>
          <b-form-input v-if="isEditing" type="text" v-model="model.subject" class="std-subject-input"/>
          <div v-else class="std-subject-title">{{ model.subject }}</div>
        </div>
        <!-- original 4-col table layout. admin-only triage row (Dev Ticket
             + Internal Notes) sits at the top with the cream+gold treatment.
             every cell renders in both view and edit modes so toggling Edit
             never shifts the layout; empty cells render the label only and
             pick up a muted treatment via std-cell--empty. on narrow
             viewports the table stacks via media-query (td→block) so every
             cell becomes a full-width row. -->
        <table class="std-table">
          <tbody>
            <!-- admin-only triage row, always rendered for admins so
                 entering edit mode doesn't shift any of the metadata rows
                 below it. -->
            <tr v-if="effectiveRole === 'admin'" class="std-row std-row--admin">
              <td class="std-cell std-cell--full-mobile" :class="{ 'std-cell--empty': !isEditing && !model.dev_ticket }">
                <h5>Dev Ticket</h5>
                <b-form-input v-if="isEditing" type="text" v-model="model.dev_ticket"/>
                <div v-else-if="model.dev_ticket" class="std-value">
                  <a v-if="jiraIssueUrl(model.dev_ticket)" :href="jiraIssueUrl(model.dev_ticket)" target="_blank" class="std-dev-ticket-link">
                    {{ jiraIssueKey(model.dev_ticket) }}
                    <i class="fa fa-external-link" aria-hidden="true"></i>
                  </a>
                  <span v-else>{{ model.dev_ticket }}</span>
                </div>
              </td>
              <td class="std-cell std-cell--full-mobile" :class="{ 'std-cell--empty': !isEditing && !model.urgency }">
                <h5>Urgency</h5>
                <b-form-select v-if="isEditing" v-model="model.urgency" :options="urgencyOptions"/>
                <div v-else-if="model.urgency" class="std-value">{{ getOptionText(urgencyOptions, model.urgency) }}</div>
              </td>
              <td class="std-cell std-cell--full-mobile" colspan="2" :class="{ 'std-cell--empty': !isEditing && !model.internal_notes }">
                <h5>Internal Notes</h5>
                <b-form-textarea v-if="isEditing" v-model="model.internal_notes" :rows="2" max-rows="5" no-resize class="std-grow-textarea"/>
                <template v-else-if="model.internal_notes">
                  <div
                    ref="internal-notes-body"
                    class="std-value std-value--multiline std-value--linkified"
                    :class="{ 'std-collapsible--collapsed': internalNotesOverflow && !internalNotesExpanded }"
                    v-html="linkifyText(model.internal_notes)"
                  ></div>
                  <button
                    v-if="internalNotesOverflow"
                    type="button"
                    class="std-expand-btn"
                    @click="internalNotesExpanded = !internalNotesExpanded"
                  >{{ internalNotesExpanded ? 'Show less' : 'Show more' }}</button>
                </template>
              </td>
            </tr>

            <tr class="std-row">
              <td class="std-cell">
                <h5>Account</h5>
                <div class="std-account-wrap">
                  <b-form-select v-if="isEditing && effectiveRole === 'admin'" v-model="model.new_tenant_id" :options="user.tenant_options"/>
                  <div v-else class="std-value">{{ getOptionText(user.tenant_options, model.tenant_id) }}</div>
                  <!-- VIP chip sits to the RIGHT of the account name, so the
                       sparkle lives at the chip's top-LEFT corner — the
                       corner closest to the account name. if this is ever
                       flipped (VIP to the left of the account), also flip
                       the sparkle to the chip's top-right so it stays
                       pointing toward the account. -->
                  <span
                    v-if="getTenantVip(model.tenant_id)"
                    class="std-vip-wrap"
                    :title="'VIP — ' + getTenantVip(model.tenant_id).vip_reason"
                  >
                    <span class="std-vip-chip">VIP</span>
                  </span>
                </div>
              </td>
              <td class="std-cell">
                <h5>Type</h5>
                <div class="std-value">{{ summary.tenant_type }} / {{ summary.telematics_type }}</div>
              </td>
              <td class="std-cell">
                <h5>Created</h5>
                <div class="std-value">{{ model.created_at | formatDateTimeSmall }}</div>
              </td>
              <td class="std-cell">
                <h5>Last Updated</h5>
                <div class="std-value">{{ model.updated_at | formatDateTimeSmall }}</div>
              </td>
            </tr>

            <tr class="std-row">
              <td class="std-cell">
                <h5>Status</h5>
                <b-form-select v-if="isEditing && effectiveRole === 'admin'" v-model="model.ticket_status" :options="statusOptions"/>
                <template v-else>
                  <div class="std-value">{{ getOptionText(statusOptions, model.ticket_status) || model.ticket_status }}</div>
                  <!-- Reopen / Approve sit next to Status; the destructive
                       Cancel Ticket lives in the page header. -->
                  <div class="std-status-actions">
                    <b-button v-if="model.ticket_status == 'completed'" size="sm" variant="outline-primary" v-on:click.prevent="update_ticket_status('new')">Reopen</b-button>
                    <b-button v-if="model.ticket_status == 'waiting'" size="sm" variant="outline-success" v-on:click.prevent="update_ticket_status('approved')">Approve</b-button>
                  </div>
                </template>
              </td>
              <td class="std-cell">
                <h5>Type</h5>
                <b-form-select v-if="isEditing" v-model="model.priority" :options="priorityOptions"/>
                <span v-else-if="getPriorityTag(model.priority)"
                      class="priority-tag priority-tag--labeled"
                      :class="[
                        getPriorityTag(model.priority).variant,
                        { 'priority-tag-muted': ['completed', 'cancelled'].includes(model.ticket_status) },
                        { 'priority-tag--strike': model.priority === 'urgent' && !['completed', 'cancelled'].includes(model.ticket_status) }
                      ]"
                      :title="getPriorityTag(model.priority).label">
                  <i class="fa" :class="getPriorityTag(model.priority).icon" aria-hidden="true"></i>
                  <span class="priority-tag-label">{{ getPriorityTag(model.priority).compactLabel }}</span>
                </span>
                <span v-else class="std-value">{{ getOptionText(priorityOptions, model.priority) }}</span>
              </td>
              <td class="std-cell">
                <h5>Feature</h5>
                <b-form-select v-if="isEditing" v-model="model.category" :options="categoryOptions"/>
                <div v-else class="std-value">{{ getOptionText(categoryOptions, model.category) }}</div>
              </td>
              <td class="std-cell">
                <div class="std-field-head">
                  <h5>Assigned To</h5>
                  <button
                    v-if="effectiveRole === 'admin'"
                    type="button"
                    class="std-inline-icon-btn"
                    :disabled="assignToMeSaving"
                    @click.stop="assignToMe"
                    :title="assignToMeSaving ? 'Assigning…' : 'Assign to me'"
                    aria-label="Assign to me"
                  >
                    <i class="fa" :class="assignToMeSaving ? 'fa-spinner fa-spin' : 'fa-user-circle-o'" aria-hidden="true"></i>
                  </button>
                </div>
                <b-form-select v-if="isEditing && effectiveRole === 'admin'" v-model="model.assigned_to" :options="assigned_options"/>
                <div v-else class="std-value std-person-display">
                  <img
                    v-if="initekTeamImage(model.assigned_to)"
                    :src="initekTeamImage(model.assigned_to)"
                    :alt="getOptionText(assigned_options, model.assigned_to)"
                    :title="getOptionText(assigned_options, model.assigned_to)"
                    class="std-team-avatar"
                  />
                  <span>{{ getOptionText(assigned_options, model.assigned_to) }}</span>
                </div>
              </td>
            </tr>

            <tr class="std-row">
              <td class="std-cell" :colspan="effectiveRole === 'admin' ? 1 : 4">
                <h5>Created By</h5>
                <b-form-select v-if="isEditing && effectiveRole === 'admin'" v-model="model.username" :options="user_options"/>
                <div v-else class="std-value std-contact-chips std-content-rail">
                  <span
                    v-if="model.username"
                    class="std-contact-chip"
                    :class="contactChipModifierClass(model.username)"
                    :title="model.username"
                  >
                    <span class="std-contact-chip-name">{{ displayUserName(model.username) }}</span>
                    <span
                      v-if="userRoleLabel(model.username)"
                      class="std-comment-thread-chip std-contact-chip-role"
                      :class="userRoleChipClass(model.username)"
                    >
                      <i v-if="userRoleIcon(model.username)" class="fa" :class="userRoleIcon(model.username)" aria-hidden="true"></i>
                      {{ userRoleLabel(model.username) }}
                    </span>
                    <span
                      v-else-if="isExternalContact(model.username)"
                      class="std-comment-thread-chip std-contact-chip-role std-contact-chip-role--external"
                    >
                      <i class="fa fa-user-o" aria-hidden="true"></i>
                      External
                    </span>
                  </span>
                </div>
              </td>
              <td v-if="effectiveRole === 'admin'" class="std-cell" :class="{ 'std-cell--empty': !isEditing && !model.creator_role }">
                <h5>Creator Role</h5>
                <b-form-input v-if="isEditing" type="text" v-model="model.creator_role"/>
                <div v-else-if="model.creator_role" class="std-value">{{ model.creator_role }}</div>
              </td>
              <td v-if="effectiveRole === 'admin'" class="std-cell" :class="{ 'std-cell--empty': !isEditing && !model.estimated_hours }">
                <h5>Estimated Dev</h5>
                <div class="std-hours-row">
                  <b-form-input v-if="isEditing" type="text" v-model="model.estimated_hours" class="std-hours-input"/>
                  <span v-else-if="model.estimated_hours" class="std-value std-value--inline">{{ model.estimated_hours }}</span>
                  <span v-if="model.estimated_hours" class="std-hours-unit">h<span class="std-hours-cost">{{ cost_estimate }}</span></span>
                </div>
              </td>
              <td v-if="effectiveRole === 'admin'" class="std-cell" :class="{ 'std-cell--empty': !isEditing && !model.actual_hours }">
                <h5>Actual Dev</h5>
                <div class="std-hours-row">
                  <b-form-input v-if="isEditing" type="text" v-model="model.actual_hours" class="std-hours-input"/>
                  <span v-else-if="model.actual_hours" class="std-value std-value--inline">{{ model.actual_hours }}</span>
                  <span v-if="model.actual_hours" class="std-hours-unit">h<span class="std-hours-cost">{{ cost_actual }}</span></span>
                </div>
              </td>
            </tr>

            <tr class="std-row">
              <td v-if="effectiveRole === 'admin'" class="std-cell" :class="{ 'std-cell--empty': !isEditing && !displayTargetDate }">
                <h5>Target Date</h5>
                <date-picker v-if="isEditing" v-model="selected_target_date" :placeholder="selected_target_date" :config="options" class="std-date-picker"/>
                <div v-else-if="displayTargetDate" class="std-value">{{ displayTargetDate }}</div>
              </td>
              <td class="std-cell" :colspan="effectiveRole === 'admin' ? 2 : 3" :class="{ 'std-cell--empty': !isEditing && !model.additional_contacts }">
                <h5>Additional Contacts<span v-if="isEditing" class="std-label-hint"> (comma separated emails)</span></h5>
                <b-form-input v-if="isEditing" type="text" v-model="model.additional_contacts"/>
                <div v-else-if="model.additional_contacts" class="std-value std-contact-chips std-content-rail">
                  <span
                    v-for="(email, i) in additionalContactsList"
                    :key="i"
                    class="std-contact-chip"
                    :class="contactChipModifierClass(email)"
                    :title="email"
                  >
                    <span class="std-contact-chip-name">{{ displayUserName(email) }}</span>
                    <span
                      v-if="userRoleLabel(email)"
                      class="std-comment-thread-chip std-contact-chip-role"
                      :class="userRoleChipClass(email)"
                    >
                      <i v-if="userRoleIcon(email)" class="fa" :class="userRoleIcon(email)" aria-hidden="true"></i>
                      {{ userRoleLabel(email) }}
                    </span>
                    <span
                      v-else-if="isExternalContact(email)"
                      class="std-comment-thread-chip std-contact-chip-role std-contact-chip-role--external"
                    >
                      <i class="fa fa-user-o" aria-hidden="true"></i>
                      External
                    </span>
                  </span>
                </div>
              </td>
              <td class="std-cell" :class="{ 'std-cell--empty': !isEditing && !model.cc_admins }">
                <h5>CC Account Admins</h5>
                <b-form-checkbox
                  v-if="isEditing || model.cc_admins"
                  v-model="model.cc_admins"
                  :disabled="!isEditing"
                />
              </td>
            </tr>

            <tr class="std-row">
              <td class="std-cell" colspan="4" :class="{ 'std-cell--empty': !isEditing && !model.description }">
                <!-- description is the body of the ticket. tinted block
                     + steelblue rail visually paired with Created By /
                     Additional Contacts (same treatment) so the meaty,
                     people-and-content fields read as related. palette
                     mirrors the historical unread-notification rail. -->
                <h5>Description<span v-if="isEditing" class="std-label-hint"> (text only)</span></h5>
                <b-form-textarea v-if="isEditing" v-model="model.description" :rows="6" max-rows="24" no-resize class="support-ticket-description std-grow-textarea"/>
                <div v-else-if="model.description" class="std-content-rail">
                  <div
                    ref="description-body"
                    class="std-value std-value--multiline std-value--linkified"
                    :class="{ 'std-collapsible--collapsed': descriptionOverflow && !descriptionExpanded }"
                    v-html="linkifyText(model.description)"
                  ></div>
                  <button
                    v-if="descriptionOverflow"
                    type="button"
                    class="std-expand-btn"
                    @click="descriptionExpanded = !descriptionExpanded"
                  >{{ descriptionExpanded ? 'Show less' : 'Show more' }}</button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>

        <div v-if="isEditing" class="std-edit-footer">
          <b-button variant="success" size="sm" @click="saveSupportTicket(true)">Save</b-button>
          <b-button v-if="effectiveRole === 'admin'" variant="success" size="sm" @click="saveSupportTicket(false)">Save (no email)</b-button>
          <b-button variant="outline-secondary" size="sm" @click="cancelEditMode">Cancel</b-button>
        </div>
      </form>
    </div>

    <!-- attachments section. in edit mode the standard "Attachments"
         header + hairline divider gives the section explicit framing
         alongside the primary Add Attachment(s) button. in read-only
         the header is dropped — the outline button + grid sit directly
         under the description so the page stays compact (the affordance
         is self-evident from the button label). std-section-card--headless
         tightens the card's top margin to match. -->
    <div v-if="!isLoading && model" class="std-section-card" :class="{ 'std-section-card--headless': !isEditing }">
      <div v-if="isEditing" class="std-section-header">
        <h4 class="std-section-title">Attachments</h4>
      </div>
      <input
        ref="ticketAttachmentInput"
        type="file"
        multiple
        style="display: none"
        @change="handleTicketAttachmentUpload"
      />
      <div class="d-flex align-items-center flex-wrap gap-2 mb-2">
        <!-- variant flips by mode: primary (filled blue) while editing —
             attaching is part of the active save workflow, peer to the
             Save button — and outline-secondary in view mode where adding
             a file later is an additive, secondary action (peer to the
             comment composer's outline Add Attachment(s)). -->
        <b-button
          v-if="canManageTicketAttachments"
          size="sm"
          :variant="isEditing ? 'primary' : 'outline-secondary'"
          @click="$refs.ticketAttachmentInput.click()"
          :disabled="ticketAttachmentBusy"
        >
          <i class="fa fa-paperclip" aria-hidden="true"></i> Add Attachment(s)
        </b-button>
        <b-button
          v-if="canManageTicketAttachments && ticketAttachments.length > 0"
          size="sm"
          variant="outline-secondary"
          @click="toggleAllTicketAttachments"
          :disabled="ticketAttachmentBusy"
        >
          {{ allTicketAttachmentsSelected ? 'Clear Selection' : 'Select All' }}
        </b-button>
        <b-button
          v-if="canManageTicketAttachments && selectedTicketAttachments.length > 0"
          size="sm"
          variant="danger"
          @click="deleteSelectedTicketAttachments"
          :disabled="ticketAttachmentBusy"
        >
          <span v-if="isDeletingTicketAttachments"><b-spinner small></b-spinner> Deleting...</span>
          <span v-else>Delete Selected ({{ selectedTicketAttachments.length }})</span>
        </b-button>
        <b-button
          v-if="canManageTicketAttachments && ticketAttachments.length > 0"
          size="sm"
          variant="outline-danger"
          @click="deleteAllTicketAttachments"
          :disabled="ticketAttachmentBusy"
        >
          Delete All
        </b-button>
        <!-- empty-state hint sits inline next to Add Attachment(s) so the
             zero-attachments view doesn't burn a whole row on the message.
             only renders when no existing attachments and nothing staged
             for upload — same condition the standalone block had. -->
        <span
          v-if="ticketAttachments.length === 0 && ticketAttachmentUploadItems.length === 0"
          class="text-muted small"
        >
          No attachments yet.
        </span>
      </div>
      <div v-if="ticketAttachments.length > 0 || ticketAttachmentUploadItems.length > 0" class="attachment-grid">
        <div
          v-for="attachment in ticketAttachments"
          :key="attachment"
          class="attachment-item existing-attachment"
          :class="{ 'attachment-selected': selectedTicketAttachments.includes(attachment) }"
        >
          <label v-if="canManageTicketAttachments" class="attachment-selector">
            <input
              type="checkbox"
              :checked="selectedTicketAttachments.includes(attachment)"
              @change="toggleTicketAttachmentSelection(attachment)"
            />
          </label>
          <img
            v-if="isImage(attachment)"
            :src="attachment"
            class="attachment-thumbnail attachment-thumbnail--clickable"
            :alt="extractFileNameFromUpload(attachment)"
            :title="extractFileNameFromUpload(attachment)"
            @click="openLightbox(attachment, extractFileNameFromUpload(attachment))"
          />
          <a v-else :href="attachment" target="_blank" class="attachment-file-link">
            <div class="attachment-file">
              <span style="font-size: 1.4rem;">&#128196;</span>
              <span class="attachment-filename">{{extractFileNameFromUpload(attachment)}}</span>
            </div>
          </a>
        </div>
        <div
          v-for="item in ticketAttachmentUploadItems"
          :key="item.id"
          class="attachment-item queued-attachment"
          :class="'attachment-state-' + item.status"
        >
          <button
            v-if="item.status === 'failed'"
            type="button"
            class="attachment-remove"
            @click="removePendingTicketAttachment(item.id)"
          >
            &times;
          </button>
          <img
            v-if="item.previewUrl"
            :src="item.previewUrl"
            class="attachment-thumbnail"
            :alt="item.name"
          />
          <div v-else class="attachment-file">
            <span style="font-size: 1.4rem;">&#128196;</span>
            <span class="attachment-filename">{{ item.name }}</span>
          </div>
          <div v-if="item.status !== 'uploaded'" class="attachment-meta">
            <div class="attachment-status">
              <span v-if="item.status === 'queued'">Waiting</span>
              <span v-else-if="item.status === 'uploading'">Uploading {{ item.progress }}%</span>
              <span v-else>Upload failed</span>
            </div>
            <b-progress
              v-if="item.status === 'uploading'"
              :value="item.progress"
              :max="100"
              height="6px"
              class="mt-2"
            />
            <div v-if="item.error" class="attachment-error">{{ item.error }}</div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="!isLoading && model" class="std-section-card">
      <div class="std-section-header">
        <div class="std-section-header-text">
          <h4 class="std-section-title">Comments</h4>
          <div class="std-section-subtitle">{{ commentsSubtitle }}</div>
        </div>
      </div>

      <!-- new comment composer — textarea spans full width; attachment
           picker + Post button sit on their own row below so narrow
           viewports don't squeeze the textarea into a sliver. -->
      <div class="std-comment-composer" :class="{ 'std-comment-composer--posting': wait_for_posting }">
        <label class="std-comment-composer-label">New Comment</label>
        <div class="std-comment-composer-body">
          <b-form-textarea v-model="newComment" :rows="2" max-rows="6" no-resize class="std-comment-textarea" :disabled="wait_for_posting"/>
          <div v-if="wait_for_posting" class="std-comment-composer-overlay" aria-live="polite" aria-busy="true">
            <b-spinner small></b-spinner>
            <span>Posting comment…</span>
          </div>
        </div>
        <!-- hidden file input + styled Add Attachment(s) button matches the
             main ticket Attachments section. selected files render as small
             removable chips under the button so the user can unstage them
             before posting. -->
        <input
          ref="commentAttachmentInput"
          id="comment_attachments"
          type="file"
          multiple
          style="display: none"
          :disabled="wait_for_posting"
          @change="handleCommentFileUpload"
        />
        <div class="std-comment-composer-row">
          <b-button size="sm" variant="outline-secondary" :disabled="wait_for_posting" @click="$refs.commentAttachmentInput.click()">
            <i class="fa fa-paperclip" aria-hidden="true"></i> Add Attachment(s)
          </b-button>
          <b-button
            type="button"
            variant="primary"
            size="sm"
            class="std-comment-post"
            :disabled="wait_for_posting"
            @click="postNewComment"
          >
            <b-spinner v-if="wait_for_posting" small class="std-comment-post-spinner"></b-spinner>
            <i v-else class="fa fa-paper-plane-o" aria-hidden="true"></i>
            {{ wait_for_posting ? 'Posting…' : 'Post Comment' }}
          </b-button>
        </div>
        <div v-if="commentAttachments.length > 0" class="std-comment-attachment-chips">
          <span
            v-for="(file, idx) in commentAttachments"
            :key="`cmt-att-${idx}-${file.name}`"
            class="std-comment-attachment-chip"
          >
            <span class="std-comment-attachment-chip-name">{{ file.name }}</span>
            <button type="button" class="std-comment-attachment-chip-remove" :disabled="wait_for_posting" @click="removeCommentAttachment(idx)" aria-label="Remove attachment">&times;</button>
          </span>
        </div>
      </div>

      <!-- message-board style thread: each comment is a flat card with
           a pastel-filled puck avatar (notification design language),
           name + optional role/self chips + relative timestamp header,
           then prose body, then optional attachment strip. comments are
           grouped by day bucket (Today / Yesterday / Earlier this week /
           Older) matching the notification list so long threads are
           easy to scan. own comments are visually identical to others
           (the "You" chip in the header is the only differentiator). -->
      <div v-if="preparedComments.length > 0" class="std-comment-thread">
        <section v-for="g in groupedComments" :key="g.key" class="std-comment-group">
          <div class="std-comment-group-header">{{ g.label }}</div>
          <div
            v-for="item in g.items"
            :key="item.comment_id"
            class="std-comment-thread-item"
            :class="{
              'std-comment-thread-item--staff': isStaffComment(item),
            }"
          >
            <div class="std-comment-thread-head">
              <!-- Initek staff show profile photos from the about-us page;
                   everyone else keeps the initials avatar. role is carried
                   by the chip next to the name, not by avatar color. -->
              <img
                v-if="commentTeamImage(item)"
                :src="commentTeamImage(item)"
                :alt="commentDisplayName(item)"
                :title="commentDisplayName(item)"
                class="std-comment-thread-avatar std-comment-thread-avatar--photo"
              />
              <span
                v-else
                class="std-comment-thread-avatar"
                :title="commentDisplayName(item)"
              >{{ commentInitials(item) }}</span>
              <div class="std-comment-thread-meta">
                <div class="std-comment-thread-name-row">
                  <span class="std-comment-thread-name">{{ commentDisplayName(item) }}</span>
                  <!-- non-admin roles collapse to icon-only (matches the
                       priority-tag--icon-only treatment in support_ticket_list);
                       label moves to title/aria-label for hover + a11y.
                       Initek Support chips stay full — icon + label — so
                       customers reading the thread see at a glance which
                       posts come from Fleet Integrations without needing
                       to hover. the role palette (via --role-X modifier)
                       keeps every chip color-coded either way. -->
                  <span v-if="commentRoleLabel(item)"
                        class="std-comment-thread-chip"
                        :class="[
                          commentRoleChipClass(item),
                          { 'std-comment-thread-chip--icon-only': commentRole(item) !== 'admin' }
                        ]"
                        :title="commentRoleLabel(item)"
                        :aria-label="commentRoleLabel(item)">
                    <i v-if="commentRoleIcon(item)" class="fa" :class="commentRoleIcon(item)" aria-hidden="true"></i>
                    <template v-if="commentRole(item) === 'admin'">{{ commentRoleLabel(item) }}</template>
                  </span>
                  <span v-if="item.username === user.username" class="std-comment-thread-chip std-comment-thread-chip--you">You</span>
                </div>
                <span class="std-comment-thread-time"
                      :title="item.updated_at && item.updated_at !== item.created_at
                        ? ('posted ' + fullCommentTime(item.created_at) + ' · edited ' + fullCommentTime(item.updated_at))
                        : fullCommentTime(item.created_at)">{{ absoluteCommentTime(item.created_at) }}<span v-if="item.updated_at && item.updated_at !== item.created_at"> &middot; edited</span></span>
              </div>
              <div v-if="!item.editing" class="std-comment-thread-actions">
                <button
                  v-if="canEditComment(item)"
                  type="button"
                  class="std-comment-icon-btn"
                  @click="startEdit(item)"
                  aria-label="Edit comment"
                  title="Edit"
                >
                  <i class="fa fa-pencil" aria-hidden="true"></i>
                </button>
                <button
                  v-if="canDeleteComment(item)"
                  type="button"
                  class="std-comment-icon-btn std-comment-icon-btn--danger"
                  @click="deleteComment(item)"
                  aria-label="Delete comment"
                  title="Delete"
                >
                  <i class="fa fa-trash-o" aria-hidden="true"></i>
                </button>
              </div>
            </div>
            <div class="std-comment-thread-body">
              <template v-if="item.editing">
                <b-form-textarea
                  v-model="item.tempComment"
                  class="mb-2"
                  :rows="3"
                  max-rows="12"
                  no-resize
                ></b-form-textarea>
                <b-button size="sm" variant="primary" @click="saveComment(item)">Save</b-button>
                <b-button size="sm" variant="outline-secondary" class="ml-2" @click="cancelEdit(item)">Cancel</b-button>
              </template>
              <div
                v-else
                :ref="`comment-body-${item.comment_id}`"
                class="std-comment-thread-text"
                :class="{ 'std-collapsible--collapsed': commentOverflow[item.comment_id] && !item.commentExpanded }"
                v-html="add_links(normalizeCommentLines(item.comment))"
              ></div>
              <button
                v-if="!item.editing && commentOverflow[item.comment_id]"
                type="button"
                class="std-expand-btn"
                @click="toggle_div(item, 'commentExpanded')"
              >{{ item.commentExpanded ? 'Show less' : 'Show more' }}</button>
            </div>
            <div v-if="item.attachments && item.attachments.length" class="std-comment-thread-attachments">
              <template v-for="attachment in item.attachments">
                <img
                  v-if="isImage(attachment)"
                  :key="'img-' + attachment"
                  :src="attachment"
                  class="std-comment-thread-thumb std-comment-thread-thumb--clickable"
                  :alt="extractFileNameFromUpload(attachment)"
                  :title="extractFileNameFromUpload(attachment)"
                  @click="openLightbox(attachment, extractFileNameFromUpload(attachment))"
                />
                <a
                  v-else
                  :key="attachment"
                  :href="attachment"
                  target="_blank"
                  class="std-comment-thread-attachment"
                  :title="extractFileNameFromUpload(attachment)"
                >
                  <span class="std-comment-thread-file">
                    <span class="std-comment-thread-file-icon">&#128196;</span>
                    <span class="std-comment-thread-file-name">{{ extractFileNameFromUpload(attachment) }}</span>
                  </span>
                </a>
              </template>
            </div>
          </div>
        </section>
      </div>

      <!-- empty state mirrors the notification list's centered icon +
           title pattern so the two surfaces share the same "nothing
           here yet" visual language. -->
      <div v-if="preparedComments.length === 0" class="std-comment-empty">
        <i class="fa fa-comments-o" aria-hidden="true"></i>
        <div class="std-comment-empty-title">No comments yet</div>
        <div class="std-comment-empty-sub">Post a comment above to start the conversation.</div>
      </div>
    </div>

    <div v-if="!isLoading && effectiveRole === 'admin' && history" class="std-section-card">
      <div class="std-section-header std-section-header--clickable std-section-header--admin" @click="toggle_div(history,'isHistoryVisible')">
        <h4 class="std-section-title">Ticket History</h4>
        <i class="fa" :class="history.isHistoryVisible ? 'fa-chevron-up' : 'fa-chevron-down'" aria-hidden="true"></i>
      </div>
      <!-- history table — follows the canonical b-table pattern from
           support_ticket_list (stl-root wrapper + dense 13px rows,
           horizontal separators, sticky-header, steelblue sort arrows,
           hover highlight). see memory/standard_table_pattern.md. -->
      <div v-if="history.isHistoryVisible" class="stl-root std-history-wrap">
        <div class="stl-table-wrap">
          <div class="stl-table-inner">
            <b-table id="history_table"
                     :items="history"
                     :fields="historyFields"
                     :primary-key="'comment_id'"
                     sort-by="processed_time"
                     :sort-desc="true">
              <template slot="processed_time" slot-scope="row">
                {{ row.item.processed_time | formatDateTimeSmall }}
              </template>
              <template slot="updated_by" slot-scope="row">
                <span v-if="row.item.updated_by">{{ displayUserName(row.item.updated_by) }}</span>
              </template>
              <template slot="diff" slot-scope="row">
                <span v-if="row.item.diff">
                  <span v-if="row.item.diff.subject">Subject: {{ row.item.diff.subject }}<br/></span>
                  <span v-if="row.item.diff.assigned_to">Assigned To: {{getOptionText(assigned_options, row.item.diff.assigned_to)}}<br/></span>
                  <span v-if="row.item.diff.urgency">Urgency: {{getOptionText(urgencyOptions, row.item.diff.urgency)}}<br/></span>
                  <span v-if="row.item.diff.ticket_status">Status: {{getOptionText(statusOptions,row.item.diff.ticket_status)}}<br/></span>
                  <span v-if="row.item.diff.description">Description: {{row.item.diff.description}}<br/></span>
                </span>
                <span v-else>
                  Subject: {{ row.item.subject }}<br/>
                  Description: {{row.item.description}}
                </span>
              </template>
            </b-table>
          </div>
        </div>
      </div>
    </div>

    <!-- lightbox modal for full-size image preview -->
    <b-modal
      id="image-lightbox"
      :title="lightboxAlt"
      size="xl"
      centered
      hide-footer
      body-class="std-lightbox-body"
    >
      <img :src="lightboxSrc" :alt="lightboxAlt" class="std-lightbox-img" />
    </b-modal>
  </div>

</template>

<script>
import {
  delete_support_ticket_attachments,
  delete_support_ticket_comment,
  get_support_ticket, get_primary_users,
  get_support_ticket_assist_export,
  update_support_ticket,
  update_support_ticket_comment,
  presign_support_ticket_attachment,
  finalize_support_ticket_attachment
} from '../networking/apis'
import { mapState } from "vuex"
import moment from "moment"
import {justDate} from "@/helpers/dates";
import { initekTeamImageForUsername } from "@/helpers/initek_team_images";
import { roleLabelForCustomer, roleIconForCustomer } from "@/helpers/role_display";

export default {
  name: 'SupportTicketDetails',
  data () {
    return {
      selected_tenant_id: null,
      tenant_id: null,
      ticket_id: null,
      history: [],
      summary: {},            // The support ticket data
      model: {},            // The support ticket data
      // skeleton-loader gate: true on first mount until load_data
      // finishes (success or failure). drives the skeleton placeholder
      // shown in place of the form so users see ticket-shaped grays
      // instead of a blank "Support Ticket #" header.
      isLoading: true,
      descriptionExpanded: false,
      descriptionOverflow: false,
      internalNotesExpanded: false,
      internalNotesOverflow: false,
      commentOverflow: {},
      newComment: '',       // New comment text
      commentAttachments: [],      // New comment file attachments
      ticketAttachmentUploadItems: [],
      selectedTicketAttachments: [],
      wait_for_posting: false,
      isUploadingTicketAttachments: false,
      isDeletingTicketAttachments: false,
      selected_target_date: null,
      user_options: [],
      // static/edit mode. detail page opens in read-only "static" mode so
      // users can scan the ticket without fighting form controls; clicking
      // the pencil icon snapshots the model and flips to edit mode, where
      // Save (writes the diff) or Cancel (restores the snapshot) ends the
      // session.
      isEditing: false,
      assignToMeSaving: false,
      _originalModel: null,
      lightboxSrc: null,
      lightboxAlt: '',
      // "Assist" export button — sys-admin only, copies a markdown brief
      // of this ticket (triage facts, comments, attachments, category-
      // specific investigation pointers) to the clipboard for handoff to
      // an AI coding agent (Claude Code, Cursor, etc.). all build logic
      // lives server-side so internal-only paths/playbooks don't ship in
      // the JS bundle.
      assistExporting: false,
      assistExported: false,
      // narrow viewport flag — matches the 720px breakpoint where the
      // form table collapses to stacked rows. on mobile we tuck the
      // sys-admin-only "Agent" export into the kebab to keep the
      // header action row breathable; on desktop it stays as its
      // own gold button next to Edit. updated reactively via
      // matchMedia in mounted(); see beforeDestroy() for cleanup.
      isMobileViewport: false,
      _mobileMql: null,
      _mobileMqlHandler: null,
      commentFields: [
        { key: 'created_at', label: 'Added At', sortable: true, thStyle: { width: "150px" } },
        { key: 'username', label: 'Added By', sortable: true, thStyle: { width: "150px" } },
        { key: 'comment',    label: 'Comments', sortable: false },
        { key: 'actions',    label: 'Actions', sortable: false, thStyle: { width: "100px" } }
      ],
      historyFields: [
        { key: 'processed_time', label: 'Update Time', sortable: true, thStyle: { width: "160px" } },
        { key: 'updated_by', label: 'Update By', sortable: true, thStyle: { width: "160px" } },
        { key: 'diff', label: 'Changes Made', sortable: true, thStyle: { width: "600px" } },
      ],
      categoryOptions: [
        {value: 'general', text: 'General'},
        {value: 'actuals', text: 'Actuals'},
        {value: 'alerts', text: 'Alerts'},
        {value: 'maps', text: 'Map Views'},
        {value: 'reports', text: 'View & Reports'},
        {value: 'vsm', text: 'Vehicle Status Manager'},
        {value: 'data', text: 'Data Services'},
        {value: 'fleet-assistant', text: 'Fleet Assistant'},
      ],
      priorityOptions: [
        {value: 'question', text: 'Question/Request'},
        {value: 'minor', text: 'Minor Bug'},
        {value: 'urgent', text: 'Major Bug / System Issue'},
        {value: 'enhancement', text: 'Enhancement Request'},
        {value: 'feature', text: 'New Feature Request'},
      ],
      urgencyOptions: [
        { value: null, text: 'Unset' },
        { value: 'low', text: 'Low' },
        { value: 'medium', text: 'Medium' },
        { value: 'high', text: 'High' },
        { value: 'emergency', text: 'Emergency' },
      ],
      statusOptions: [
        { value: 'new', text: 'New' },
        { value: 'queued', text: 'Queued' },
        { value: 'review', text: 'Under Review' },
        { value: 'progress', text: 'In Progress' },
        { value: 'waiting', text: 'Waiting On Customer' },
        { value: 'approved', text: 'Customer Approved' },
        { value: 'testing', text: 'Customer Testing' },
        { value: 'blocked', text: 'Blocked' },
        { value: 'cancelled', text: 'Cancelled' },
        { value: 'completed', text: 'Completed' },
      ],
      assigned_options: [
        { value: null, text: 'Unassigned' },
        { value: 'ben@initekconsulting.com', text: 'Ben ODay' },
        // jen is a legacy short-username account (no email-as-username);
        // keeping her login as "jen" is intentional — see userRole() for
        // the matching staff-detection logic.
        { value: 'jen', text: 'Jen' },
        { value: 'chaz@initekconsulting.com', text: 'Chaz Watson' },
        { value: 'kai@initekconsulting.com', text: 'Kai Klein' },
        { value: 'evan@initekconsulting.com', text: 'Evan Anderson' },
      ],
      options: {
        format: 'MM-DD-YYYY',
        useCurrent: true,
        minDate: moment(),
        maxDate: moment().add(180, 'days'),
        keepOpen: false,
        showClear: true,
        showClose: true,
        toolbarPlacement: 'top',
      },
    }
  },
  async created() {
    this.selected_tenant_id = this.user.tenant
    // prefer the short-form `ticket_id` query key (emitted by the current
    // backend URL builders + help_center nav). fall back to legacy `id`
    // so old /support_ticket_details?tenant_id=X&id=Y links continue to
    // work through the router redirect.
    this.ticket_id = this.$route.query.ticket_id || this.$route.query.id
    // tenant_id is a *hint* for the backend lookup. prefer the URL value
    // (always set when navigating from the list, via the router redirect).
    // fall back to the user's active tenant as a last resort for direct
    // links (Slack, email) that don't carry tenant_id in the URL.
    this.tenant_id = this.$route.query.tenant_id || (this.user && this.user.tenant) || ''

    await this.load_data()

    // re-sync tenant_id to the ticket's real tenant now that the model is
    // populated. covers both the stale-Slack-URL case (URL had the wrong
    // tenant) and the no-tenant-in-URL case (hint was user.tenant).
    if (this.model && this.model.tenant_id) {
      this.tenant_id = this.model.tenant_id
    }

    if (this.effectiveRole === 'admin' && this.model) {
      await this.load_users()
    }
  },
  mounted() {
    // reactive viewport tracking for the Agent export placement
    // (standalone button on desktop, kebab item on mobile). 720px
    // matches the breakpoint where the form table collapses.
    this._mobileMql = window.matchMedia('(max-width: 720px)')
    this.isMobileViewport = this._mobileMql.matches
    this._mobileMqlHandler = (e) => { this.isMobileViewport = e.matches }
    this._mobileMql.addEventListener('change', this._mobileMqlHandler)
  },
  beforeDestroy() {
    this.ticketAttachmentUploadItems.forEach(item => {
      if (item.previewUrl) {
        URL.revokeObjectURL(item.previewUrl)
      }
    })
    if (this._mobileMql && this._mobileMqlHandler) {
      this._mobileMql.removeEventListener('change', this._mobileMqlHandler)
    }
  },
  computed: {
    ...mapState({
      user: state => state.profile.user
    }),
    // respect MUGGLE "view as non-admin" so admins previewing the UI as
    // a customer don't see admin-only fields (triage row, admin-only
    // edit controls, ticket history). gate all visibility on
    // effectiveRole, not user.role. backend / fetch calls can still use
    // user.role since muggle is display-only.
    effectiveRole() {
      const viewAs = this.$store && this.$store.state && this.$store.state.profile && this.$store.state.profile.view_as
      if (this.user && this.user.role === 'admin' && viewAs === 'non-admin') return 'customer'
      return this.user && this.user.role
    },
    // additional_contacts is stored as a comma-separated string
    // (sometimes with stray whitespace, blank entries from double-commas,
    // etc). normalize to an array for chip rendering — keep the raw
    // string as the source of truth on the model so save/edit round-trip
    // still works against the legacy format.
    additionalContactsList() {
      const raw = this.model && this.model.additional_contacts
      if (!raw) return []
      return String(raw)
        .split(',')
        .map(s => s.trim())
        .filter(Boolean)
    },
    // guard against the legacy "Invalid date" string that earlier versions
    // of the save-path wrote into model.estimated_finish_date when the
    // watcher picked up a null/unparseable value. also filter empty and
    // filter values that don't parse as a real date — we don't want to
    // render literal junk in the Target Date cell. returns '' to signal
    // "don't show this row at all" to the template.
    displayTargetDate() {
      const raw = this.model && this.model.estimated_finish_date
      if (!raw || raw === 'Invalid date') return ''
      // accept whatever the backend gave us; if it's a parseable date in
      // any common format, render the stored string as-is so we don't
      // reformat behind the user. if it's garbage, hide it.
      const m = moment(raw, ['MM-DD-YYYY', moment.ISO_8601], true)
      return m.isValid() ? raw : ''
    },
    ticketAttachments() {
      if (Array.isArray(this.model.attachments)) {
        return this.model.attachments
      }
      // backward compat: surface attachments from the "initial attachments" or "Email attachments" comment
      const comments = this.model.comments || []
      const initialComment = comments.find(c => this.isLegacyAttachmentPlaceholder(c))
      return initialComment ? initialComment.attachments : []
    },
    allTicketAttachmentsSelected() {
      return this.ticketAttachments.length > 0 && this.selectedTicketAttachments.length === this.ticketAttachments.length
    },
    ticketAttachmentBusy() {
      return this.isUploadingTicketAttachments || this.isDeletingTicketAttachments
    },
    canManageTicketAttachments() {
      return !!this.model && !!this.model.id
    },
    preparedComments() {
      return (this.model.comments || []).filter(comment => !this.isLegacyAttachmentPlaceholder(comment))
    },
    // newest-first so the most recent message lands at the top of the
    // thread (matches the prior b-table's sort-desc on created_at).
    sortedComments() {
      return [...this.preparedComments].sort((a, b) => {
        const ta = a.created_at ? new Date(a.created_at).getTime() : 0
        const tb = b.created_at ? new Date(b.created_at).getTime() : 0
        return tb - ta
      })
    },
    // day-bucket grouping mirrors notifications_list.vue so long threads
    // are easy to scan. sortedComments is already newest-first, so today
    // appears at the top and older buckets unfold below.
    groupedComments() {
      const startOfToday = moment().startOf('day')
      const startOfYesterday = moment().subtract(1, 'day').startOf('day')
      const startOfWeek = moment().startOf('week')
      const buckets = { today: [], yesterday: [], week: [], older: [] }
      for (const c of this.sortedComments) {
        const d = c.created_at ? moment(c.created_at) : null
        if (!d || !d.isValid()) { buckets.older.push(c); continue }
        if (d.isSameOrAfter(startOfToday)) buckets.today.push(c)
        else if (d.isSameOrAfter(startOfYesterday)) buckets.yesterday.push(c)
        else if (d.isSameOrAfter(startOfWeek)) buckets.week.push(c)
        else buckets.older.push(c)
      }
      const groups = []
      if (buckets.today.length) groups.push({ key: 'today', label: 'Today', items: buckets.today })
      if (buckets.yesterday.length) groups.push({ key: 'yesterday', label: 'Yesterday', items: buckets.yesterday })
      if (buckets.week.length) groups.push({ key: 'week', label: 'Earlier this week', items: buckets.week })
      if (buckets.older.length) groups.push({ key: 'older', label: 'Older', items: buckets.older })
      return groups
    },
    // subtitle under the Comments section title: total count + optional
    // "N from you" so admins glancing at a long thread can see at a
    // glance how much is their own input.
    commentsSubtitle() {
      const total = this.preparedComments.length
      if (total === 0) return 'No comments yet'
      const mine = this.preparedComments.filter(c => c.username === this.user.username).length
      const base = total === 1 ? '1 comment' : `${total} comments`
      return mine > 0 ? `${base} \u00b7 ${mine} from you` : base
    },
    cost_estimate() {
      if (this.model.estimated_hours) {
        return ' ($' + this.model.estimated_hours * 150 + ')';
      }
    },
    cost_actual() {
      if (this.model.actual_hours) {
        return ' ($' + this.model.actual_hours * 150 + ')';
      }
    },
    // anyone viewing the ticket can enter edit mode — the per-field
    // readonly rules still apply once inside. reuse the existing role
    // gates on individual controls rather than pre-blocking entry.
    canEdit() {
      return !!this.model && !!this.model.id
    },
    // true when the ticket is in a state where cancelling is a valid
    // next step. used to hide the overflow kebab entirely (instead of
    // rendering an empty menu) when the ticket is already in a
    // terminal state.
    canCancelTicket() {
      const s = this.model && this.model.ticket_status
      return !!s && s !== 'completed' && s !== 'cancelled'
    },
  },
  methods: {
    normalizeCommentLines(text) {
      if (text == null || text === '') return ''
      return String(text)
        .replace(/\r\n/g, '\n')
        .replace(/\n{3,}/g, '\n\n')
        .trim()
    },
    add_links(text) {
      const Rexp = /(\b(https?|ftp|file):\/\/([-A-Z0-9+&@#%?=~_|!:,.;]*)([-A-Z0-9+&@#%?\/=~_|!:,.;]*)[-A-Z0-9+&@#\/%=~_|])/ig;
      return text.replace(Rexp, "<a href='$1' target='_blank'>$1</a>");
    },
    // v-html-safe variant: HTML-escape first so user-entered `<script>`
    // or similar can't inject, then apply the url regex so urls
    // become clickable `<a>` tags. use for description / internal
    // notes view mode.
    // if the dev ticket looks like a Jira key (FI-1234, ABC-99), return
    // the Atlassian URL; otherwise null so the template renders plain
    // text. keeps free-text notes (e.g. "see Kai's task board") as-is.
    // extract a jira issue key from either a bare key ("FI-902") or a
    // full Atlassian URL ("https://initekconsulting.atlassian.net/browse/FI-902").
    // legacy tickets stored the full URL in dev_ticket; newer ones store
    // just the key. return the key if found, else null.
    jiraIssueKey(devTicket) {
      if (!devTicket) return null
      const raw = String(devTicket).trim()
      // bare key first
      let m = raw.match(/^[A-Z][A-Z0-9]+-\d+$/)
      if (m) return m[0]
      // fall back to pulling the last /browse/KEY segment from a URL
      m = raw.match(/\/browse\/([A-Z][A-Z0-9]+-\d+)(?:[\?#].*)?$/i)
      if (m) return m[1].toUpperCase()
      return null
    },
    jiraIssueUrl(devTicket) {
      const key = this.jiraIssueKey(devTicket)
      return key ? `https://initekconsulting.atlassian.net/browse/${key}` : null
    },

    linkifyText(text) {
      if (text == null || text === '') return ''
      const normalized = String(text)
        .replace(/\r\n/g, '\n')
        .replace(/\n{3,}/g, '\n\n')
        .trim()
      const escaped = normalized
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
      return this.add_links(escaped)
    },
    toggle_div(item,field) {
      item[field] = !item[field];
      this.$forceUpdate();
    },
    checkCommentOverflow() {
      // natural-height check against the std-collapsible--collapsed cap
      // (200px). using a fixed threshold instead of `scrollHeight >
      // clientHeight` means the measurement works regardless of whether
      // the `--collapsed` class is currently applied — the template now
      // gates that class on the resulting overflow flag, so the previous
      // clientHeight-based test created a chicken-and-egg deadlock
      // (class only applies if overflow; overflow only detected if class
      // applied). scrollHeight reports the natural content height in
      // both states, so this works either way.
      const MAX_COLLAPSED_HEIGHT = 200
      this.$nextTick(() => {
        const descEl = this.$refs['description-body']
        if (descEl) this.descriptionOverflow = descEl.scrollHeight > MAX_COLLAPSED_HEIGHT

        const notesEl = this.$refs['internal-notes-body']
        if (notesEl) this.internalNotesOverflow = notesEl.scrollHeight > MAX_COLLAPSED_HEIGHT

        const updated = { ...this.commentOverflow }
        for (const key in this.$refs) {
          if (!key.startsWith('comment-body-')) continue
          const el = Array.isArray(this.$refs[key]) ? this.$refs[key][0] : this.$refs[key]
          if (!el) continue
          const id = key.replace('comment-body-', '')
          updated[id] = el.scrollHeight > MAX_COLLAPSED_HEIGHT
        }
        this.commentOverflow = updated
      })
    },
    async load_users() {
      const {value: {data = []}} = await get_primary_users(this.tenant_id)
      this.user_options = data.results.map(item => ({
        value: item['username'],
        // prefer the registered first/last name so comment author
        // labels read as "Ryan Cassidy" rather than "rcassidy@...".
        // fall back to the email when the user hasn't filled in a
        // name yet. mirrors the label format used by
        // support_ticket_create.vue's assigned-to dropdown.
        text: item['first_name']
          ? (item['last_name']
              ? item['first_name'] + ' ' + item['last_name']
              : item['first_name'])
          : item['username'],
        // carry the role through so the comment thread can render a
        // role chip next to the name (Admin / Manager / Dispatch /
        // etc). getOptionText only looks at value+text, so the extra
        // field doesn't disturb existing lookups.
        role: item['role'] || null,
    }));
    },
    async load_data() {
      try {
        const results = await get_support_ticket(this.tenant_id, this.ticket_id)
        // default is {} not [] — downstream code reads data.item / .history
        // / .summary. [] would silently no-op those and crash on the first
        // model.* access below.
        const { value: { data = {} } = {} } = results || {}
        this.history = data.history || null
        this.summary = data.summary || null
        this.selectedTicketAttachments = []

        // backend now returns {item: null} when the ticket id isn't found
        // under this tenant (instead of an implicit None → 500 dance). render
        // the "not found" state from the template rather than the generic
        // "try logging in again" alert.
        if (!data.item) {
          this.model = null
          return
        }

        this.model = data.item
        this.model.cc_admins = !!this.model.cc_admins
        this.model.new_tenant_id = this.model.tenant_id
        if (this.model.estimated_finish_date)
          this.selected_target_date = justDate(moment(this.model.estimated_finish_date,'MM-DD-YYYY'))

        if (this.model.comments) {
          this.model.comments.forEach((comment, idx) => {
            this.$set(this.model.comments[idx], 'editing', false)
            this.$set(this.model.comments[idx], 'tempComment', comment.comment)
          })
        }
      } catch(e) {
        console.error('Error fetching support ticket:', e)
        this.model = null
        this.$emit('showAlert', 'Error retrieving data, try logging in again or contact support if needed', 'danger', 5000)
      } finally {
        this.isLoading = false
      }
    },
    update_ticket_status(new_status) {
      this.model.ticket_status = new_status
      this.saveSupportTicket(true)
    },
    // build an AI-coder-ready markdown brief of this ticket and copy it to
    // the clipboard. backend renders the markdown (sys-admin gated) so the
    // internal investigation playbook routing never ships in the JS
    // bundle. button shows "Building…" during the round-trip and
    // "Copied!" for 2s on success. vendor-neutral — the brief works with
    // Claude Code, Cursor, or any agent that takes a markdown context dump.
    async exportTicketForAssist() {
      if (this.assistExporting) return
      this.assistExporting = true
      try {
        const tenantForCall = this.model.tenant_id || this.user.tenant
        // hand a Promise<Blob> to ClipboardItem so the clipboard write stays
        // bound to the original click. safari refuses both writeText and
        // execCommand post-await, so this path is the only one that works
        // there. cachedMarkdown is captured inside the .then so the legacy
        // execCommand fallback below has a string to copy if the modern
        // path rejects (chrome quirk on some configurations).
        let cachedMarkdown = null
        let emptyResult = false
        const fetchPromise = get_support_ticket_assist_export(tenantForCall, this.model.id).then(result => {
          const markdown = result && result.value && result.value.data && result.value.data.markdown
          if (!markdown) {
            emptyResult = true
            return new Blob([''], { type: 'text/plain' })
          }
          cachedMarkdown = markdown
          return new Blob([markdown], { type: 'text/plain' })
        })
        let copied = false
        let modernErr = null
        if (navigator.clipboard && window.ClipboardItem && typeof navigator.clipboard.write === 'function') {
          try {
            await navigator.clipboard.write([new ClipboardItem({ 'text/plain': fetchPromise })])
            copied = true
          } catch (e) {
            modernErr = e
          }
        } else {
          // browsers without ClipboardItem still need the fetch to settle
          // before the legacy fallback runs.
          await fetchPromise
        }
        // if both copied=false and cachedMarkdown=null, the api itself
        // failed inside the promise; surface that via the outer catch.
        if (!copied && !emptyResult && !cachedMarkdown && modernErr) throw modernErr
        if (emptyResult) {
          this.$emit('showAlert', 'export returned empty content', 'warning', 4000)
          return
        }
        if (!copied && cachedMarkdown) {
          copied = this.copyTextToClipboard(cachedMarkdown)
        }
        if (copied) {
          this.assistExported = true
          setTimeout(() => { this.assistExported = false }, 2000)
        } else {
          this.$emit('showAlert', 'could not write to clipboard', 'warning', 4000)
        }
      } catch (e) {
        // execute_generator throws on non-2xx; surface a friendly message.
        const msg = (e && e.response && e.response.data && (e.response.data.message || (e.response.data.data && e.response.data.data.error))) || (e && e.message) || 'unknown error'
        this.$emit('showAlert', 'Assist export failed: ' + msg, 'danger', 5000)
      } finally {
        this.assistExporting = false
      }
    },
    // legacy fallback for browsers where navigator.clipboard.write rejects
    // post-await. synchronous textarea + execCommand('copy') still copies
    // in chrome because the gesture context is "live enough" for
    // execCommand even when the modern api is rejected. doesn't work in
    // safari — safari refuses execCommand entirely after an await, which
    // is why the ClipboardItem path runs first.
    copyTextToClipboard(text) {
      try {
        const ta = document.createElement('textarea')
        ta.value = text
        ta.setAttribute('readonly', '')
        ta.style.position = 'fixed'
        ta.style.top = '-1000px'
        ta.style.opacity = '0'
        document.body.appendChild(ta)
        const prevSelection = document.activeElement
        ta.select()
        const ok = document.execCommand('copy')
        document.body.removeChild(ta)
        if (prevSelection && typeof prevSelection.focus === 'function') prevSelection.focus()
        return ok
      } catch (e) {
        return false
      }
    },
    // cancelling a ticket is destructive and now lives behind the
    // overflow menu — add a confirm prompt so the two-step feel
    // matches the visual signalling. mirror of the customer-side
    // Cancel button behaviour in earlier versions.
    confirmCancelTicket() {
      if (!this.canCancelTicket) return
      const ok = window.confirm('Cancel this ticket? The status will change to "Cancelled" and can only be reverted by reopening.')
      if (!ok) return
      this.update_ticket_status('cancelled')
    },
    // use browser history.back() so the user can hit Forward in their
    // browser and return to the ticket. fall back to dropping ticket
    // query params when there's no prior in-app entry (deep link).
    goBackToList() {
      if (window.history.length > 1) {
        this.$router.go(-1)
        return
      }
      this.goToSupportTicketList()
    },
    // deterministic nav to the support ticket list within help_center —
    // used by the "Ticket not available" card so fresh tabs from Slack
    // (history.length === 1, back() does nothing) still land somewhere
    // useful.
    goToSupportTicketList() {
      const q = { ...this.$route.query }
      delete q.ticket_id
      delete q.tenant_id
      delete q.id
      this.$router.replace({ query: q }).catch(() => {})
    },
    async assignToMe() {
      if (this.effectiveRole !== 'admin' || !this.model || this.assignToMeSaving) return
      const me = this.user && this.user.username
      if (!me) return
      this.assignToMeSaving = true
      this.model.assigned_to = me
      try {
        // one-click assign + persist; no edit-mode hop needed.
        await this.saveSupportTicket(false)
      } finally {
        this.assignToMeSaving = false
      }
    },
    handleFormFieldDblClick(event) {
      // admins can jump straight into edit mode by double-clicking any
      // display field while in read mode. single-click stays free for
      // text selection / copy-paste so the ticket reads like a normal
      // document. keep existing link/button behavior by ignoring
      // gestures that start from interactive controls.
      if (this.isEditing || this.effectiveRole !== 'admin' || !this.canEdit) return
      const target = event && event.target
      if (!target || typeof target.closest !== 'function') return
      if (target.closest('a, button, input, textarea, select, label, [role="button"], .dropdown-menu, .std-status-actions, .std-expand-btn')) return
      if (!target.closest('.std-subject-banner, .std-cell')) return
      this.enterEditMode()
    },
    enterEditMode() {
      // snapshot the editable portion of the model. attachments/comments
      // live in their own always-interactive sections so we don't need to
      // track them here. JSON round-trip is the simplest deep clone for
      // primitive-heavy support ticket payloads.
      this._originalModel = JSON.parse(JSON.stringify({
        new_tenant_id: this.model.new_tenant_id,
        tenant_id: this.model.tenant_id,
        dev_ticket: this.model.dev_ticket,
        internal_notes: this.model.internal_notes,
        ticket_status: this.model.ticket_status,
        username: this.model.username,
        creator_role: this.model.creator_role,
        assigned_to: this.model.assigned_to,
        urgency: this.model.urgency,
        category: this.model.category,
        priority: this.model.priority,
        estimated_hours: this.model.estimated_hours,
        actual_hours: this.model.actual_hours,
        additional_contacts: this.model.additional_contacts,
        cc_admins: this.model.cc_admins,
        subject: this.model.subject,
        description: this.model.description,
      }))
      this._originalTargetDate = this.selected_target_date
      this.isEditing = true
    },
    cancelEditMode() {
      if (this._originalModel) {
        Object.assign(this.model, this._originalModel)
        this.selected_target_date = this._originalTargetDate
      }
      this._originalModel = null
      this._originalTargetDate = null
      this.isEditing = false
    },
    async saveSupportTicket (send_email) {
      try {
//        const { comments, ...ticketFields } = this.model
        this.$emit('showAlert', "Saving support ticket...", 'info')
        const response = await update_support_ticket(this.model.tenant_id, this.model, send_email)
        const message = (response && response.message) || 'Support ticket updated successfully!'
        this.$emit('showAlert', message, 'success', 1500)
        this.isEditing = false
        this._originalModel = null
        this._originalTargetDate = null
        this.load_data()
      } catch(e) {
        this.$emit('showAlert', 'Error updating data, try logging in again or contact support if needed', 'danger', 5000)
      }
    },

    handleCommentFileUpload(event) {
      // append each batch so the user can pick files across multiple
      // presses of Add Attachment(s). reset the input value afterwards
      // so picking the same filename a second time still fires change.
      const picked = Array.from(event.target.files || [])
      if (picked.length) {
        this.commentAttachments = [...this.commentAttachments, ...picked]
      }
      if (event && event.target) event.target.value = null
    },

    removeCommentAttachment(index) {
      this.commentAttachments = this.commentAttachments.filter((_, i) => i !== index)
    },

    handleTicketAttachmentUpload(event) {
      const files = event && event.target && event.target.files ? Array.from(event.target.files) : []
      if (event && event.target) {
        event.target.value = null
      }
      if (!files.length) return

      const existingKeys = new Set(this.ticketAttachmentUploadItems.map(item => `${item.name}:${item.size}`))
      const newItems = files
        .filter(file => !existingKeys.has(`${file.name}:${file.size}`))
        .map((file, index) => this.createPendingTicketAttachment(file, index))
      this.ticketAttachmentUploadItems = [...this.ticketAttachmentUploadItems, ...newItems]
      this.processPendingTicketAttachments()
    },

    createPendingTicketAttachment(file, index) {
      return {
        id: `${Date.now()}-${Math.random().toString(16).slice(2)}-${index}`,
        file,
        name: file.name,
        size: file.size,
        status: 'queued',
        progress: 0,
        error: null,
        previewUrl: file.type && file.type.startsWith('image/') ? URL.createObjectURL(file) : null,
      }
    },

    updatePendingTicketAttachment(itemId, patch) {
      const index = this.ticketAttachmentUploadItems.findIndex(item => item.id === itemId)
      if (index === -1) return
      this.$set(this.ticketAttachmentUploadItems, index, {
        ...this.ticketAttachmentUploadItems[index],
        ...patch,
      })
    },

    removePendingTicketAttachment(itemId) {
      const item = this.ticketAttachmentUploadItems.find(candidate => candidate.id === itemId)
      if (item && item.previewUrl) {
        URL.revokeObjectURL(item.previewUrl)
      }
      this.ticketAttachmentUploadItems = this.ticketAttachmentUploadItems.filter(candidate => candidate.id !== itemId)
    },

    async processPendingTicketAttachments() {
      if (this.isUploadingTicketAttachments) return

      const queuedItems = this.ticketAttachmentUploadItems.filter(item => item.status === 'queued')
      if (!queuedItems.length) return

      this.isUploadingTicketAttachments = true
      this.$emit('showAlert', 'Uploading support ticket attachments...', 'info')

      const CONCURRENCY = 3
      let index = 0
      const worker = async () => {
        while (index < queuedItems.length) {
          const item = queuedItems[index++]
          await this.uploadPendingTicketAttachment(item)
        }
      }
      await Promise.all(
        Array.from({ length: Math.min(CONCURRENCY, queuedItems.length) }, () => worker())
      )

      this.ticketAttachmentUploadItems.forEach(item => {
        if (item.status !== 'failed' && item.previewUrl) {
          URL.revokeObjectURL(item.previewUrl)
        }
      })
      this.ticketAttachmentUploadItems = this.ticketAttachmentUploadItems.filter(item => item.status === 'failed')
      this.isUploadingTicketAttachments = false
      await this.load_data()
    },

    async uploadPendingTicketAttachment(item) {
      this.updatePendingTicketAttachment(item.id, {
        status: 'uploading',
        progress: 0,
        error: null,
      })
      try {
        const file = item.file
        const filename = file.name || 'attachment'
        const contentType = file.type || 'application/octet-stream'

        const presignResp = await presign_support_ticket_attachment(this.model.tenant_id, {
          filename,
          content_type: contentType,
          file_size: file.size || 0,
        })
        const presignData = presignResp && presignResp.value && presignResp.value.data
          ? (presignResp.value.data.data || presignResp.value.data)
          : {}
        const upload = presignData.upload || {}
        const s3Url = presignData.s3_url

        const uploadForm = new FormData()
        Object.entries(upload.fields || {}).forEach(([k, v]) => uploadForm.append(k, v))
        uploadForm.append('file', file)

        await this.uploadToS3WithProgress(upload.url, uploadForm, (pct) => {
          this.updatePendingTicketAttachment(item.id, { progress: pct })
        })

        await finalize_support_ticket_attachment(this.model.tenant_id, {
          ticket_id: this.model.id,
          attachment_url: s3Url,
        })

        this.updatePendingTicketAttachment(item.id, {
          status: 'uploaded',
          progress: 100,
          error: null,
        })
      } catch (err) {
        const msg = err && err.response && err.response.data && err.response.data.error
          ? err.response.data.error
          : (err && err.message ? err.message : 'Upload failed')
        console.warn('[Support Ticket] Presign upload error', err)
        this.updatePendingTicketAttachment(item.id, {
          status: 'failed',
          progress: 0,
          error: msg,
        })
      }
    },

    uploadToS3WithProgress(url, formData, onProgress) {
      return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest()
        xhr.open('POST', url, true)
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve()
          } else {
            let detail = `S3 upload failed (HTTP ${xhr.status})`
            try {
              const xml = xhr.responseText || ''
              const codeMatch = xml.match(/<Code>([^<]+)<\/Code>/)
              const msgMatch = xml.match(/<Message>([^<]+)<\/Message>/)
              if (codeMatch || msgMatch) {
                detail = [codeMatch && codeMatch[1], msgMatch && msgMatch[1]].filter(Boolean).join(': ')
              }
            } catch (_) { /* ignore parse errors */ }
            reject(new Error(detail))
          }
        }
        xhr.onerror = () => reject(new Error('S3 upload failed (network error)'))
        xhr.upload.onprogress = (event) => {
          if (!event.lengthComputable || typeof onProgress !== 'function') return
          const pct = Math.max(0, Math.min(100, Math.round((event.loaded / event.total) * 100)))
          onProgress(pct)
        }
        xhr.send(formData)
      })
    },

    toggleTicketAttachmentSelection(attachmentUrl) {
      if (this.selectedTicketAttachments.includes(attachmentUrl)) {
        this.selectedTicketAttachments = this.selectedTicketAttachments.filter(url => url !== attachmentUrl)
      } else {
        this.selectedTicketAttachments = [...this.selectedTicketAttachments, attachmentUrl]
      }
    },

    toggleAllTicketAttachments() {
      if (this.allTicketAttachmentsSelected) {
        this.selectedTicketAttachments = []
      } else {
        this.selectedTicketAttachments = [...this.ticketAttachments]
      }
    },

    async deleteTicketAttachments(attachments) {
      try {
        this.isDeletingTicketAttachments = true
        this.$emit('showAlert', 'Deleting selected attachments...', 'info')
        const response = await delete_support_ticket_attachments(this.model.tenant_id, {
          ticket_id: this.model.id,
          attachments,
        })
        const payload = response && response.value && response.value.data ? response.value.data : {}
        this.model.attachments = payload.attachments || []
        this.selectedTicketAttachments = []
        this.$emit('showAlert', `Deleted ${payload.deleted_count || 0} attachment(s)`, 'success', 3000)
        await this.load_data()
      } catch (e) {
        this.$emit('showAlert', 'Error deleting ticket attachments, try again', 'danger', 5000)
      } finally {
        this.isDeletingTicketAttachments = false
      }
    },

    async deleteSelectedTicketAttachments() {
      const attachments = [...this.selectedTicketAttachments]
      if (!attachments.length) return
      await this.deleteTicketAttachments(attachments)
    },

    async deleteAllTicketAttachments() {
      if (!this.ticketAttachments.length) return
      await this.deleteTicketAttachments([...this.ticketAttachments])
    },

    async postNewComment() {
      if (this.wait_for_posting) return
      const text = this.normalizeCommentLines(this.newComment)
      if (!text) {
        alert('Comment cannot be empty')
        return
      }
      if (!this.model.tenant_id) {
        this.$emit('showAlert', 'Error adding comment, try again', 'danger')
        return
      }

      this.wait_for_posting = true
      try {
        const formData = new FormData()
        formData.append('comment', text)
        this.commentAttachments.forEach(file => {
          formData.append('attachments', file)
        })

        const url = process.env.VUE_APP_API_URL + 'bookings/support_ticket/comment?tenant=' + this.model.tenant_id + '&ticket_id=' + this.model.id
        await new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest()
          xhr.open('POST', url, true)
          xhr.setRequestHeader('Accept', '*/*')
          xhr.setRequestHeader('Authorization', `Bearer ${localStorage.getItem('jwt')}`)
          xhr.onloadend = () => {
            if (xhr.status === 200) {
              resolve()
            } else {
              reject(new Error('HTTP ' + xhr.status))
            }
          }
          xhr.onerror = () => reject(new Error('network error'))
          xhr.send(formData)
        })

        this.$emit('showAlert', 'Comment posted', 'success', 5000)
        this.newComment = ''
        this.commentAttachments = []
        if (this.$refs.commentAttachmentInput) {
          this.$refs.commentAttachmentInput.value = null
        }
        await this.load_data()
      } catch (e) {
        this.$emit('showAlert', 'Error adding comment, try again', 'danger', 5000)
      } finally {
        this.wait_for_posting = false
      }
    },
    setSuccessMessage(message) {
      this.$emit('showAlert', message, 'success', 5000)
    },

    // prefer the display name for a username (email). tries the Initek
    // staff roster first, then the tenant user roster (loaded via
    // load_users for admin viewers). either source may carry a first/
    // last name we can surface instead of the raw email. if nothing
    // resolves, fall back to the full email address — this covers the
    // "ticket created from email" case where the sender has no user
    // record at all, and also the "user exists but name not filled out"
    // case where user_options.text degrades back to username anyway.
    displayUserName(username) {
      if (!username) return ''
      const fromAssigned = this.getOptionText(this.assigned_options, username)
      if (fromAssigned && fromAssigned !== username) return fromAssigned
      const fromUsers = this.getOptionText(this.user_options, username)
      if (fromUsers && fromUsers !== username) return fromUsers
      return username
    },
    initekTeamImage(username) {
      return initekTeamImageForUsername(username)
    },
    // comment-thread author display. thin wrapper so the comment-thread
    // render path stays readable and handles the null-item case.
    commentDisplayName(commentItem) {
      if (!commentItem || !commentItem.username) return 'Unknown'
      return this.displayUserName(commentItem.username)
    },
    commentTeamImage(commentItem) {
      if (!commentItem || !commentItem.username) return null
      return initekTeamImageForUsername(commentItem.username)
    },
    // derive avatar initials from the display name.
    // - multi-word name ("Chaz Watson") → first + last char → CW.
    // - single-word name ("Jen") → one letter → J. applies when the user
    //   has only a first name (or only a last name) on their record —
    //   forcing two letters here gives an awkward "JE" that reads like a
    //   misspelled first name.
    // - single-word email handle ("rcassidy@coachatlantic.com") → keep
    //   the two-letter fallback from the local-part: ./_/- separators
    //   give first.last → FL; otherwise first two chars so "rcassidy" →
    //   RC. handles don't have real first/last structure, so one letter
    //   would read as unidentifiable.
    // always uppercase; empty input degrades to "?".
    commentInitials(commentItem) {
      const raw = this.commentDisplayName(commentItem)
      if (!raw) return '?'
      const trimmed = String(raw).trim()
      if (!trimmed) return '?'
      const words = trimmed.split(/\s+/).filter(Boolean)
      let pick
      if (words.length >= 2) {
        pick = words[0].charAt(0) + words[words.length - 1].charAt(0)
      } else if (trimmed.includes('@')) {
        const local = trimmed.split('@')[0]
        const parts = local.split(/[._-]+/).filter(Boolean)
        if (parts.length >= 2) {
          pick = parts[0].charAt(0) + parts[parts.length - 1].charAt(0)
        } else {
          pick = local.slice(0, 2)
        }
      } else {
        pick = trimmed.charAt(0)
      }
      return (pick || '?').toUpperCase()
    },
    // Initek staff authored this comment — used to render the amber
    // puck that matches the admin-only-row visual language elsewhere
    // in the ticket system. detects staff via the initekconsulting.com
    // domain rather than membership in the (short, hardcoded)
    // assigned_options list so new hires who comment before being
    // added to that list still read as staff.
    isStaffComment(commentItem) {
      const u = commentItem && commentItem.username
      if (!u) return false
      if (this.isStaffEmail(u)) return true
      // legacy short-username staff (e.g. "jen") — recognize via the
      // hardcoded Initek roster in assigned_options. same logic as
      // userRole() so the two stay in sync.
      return (this.assigned_options || []).some(o => o.value && o.value === u)
    },
    // resolve a user's role from a username alone. sys admins are pinned
    // via the initekconsulting.com domain so we can tag them even when
    // user_options hasn't loaded (non-admin viewers). for the viewer's
    // own username we trust the logged-in user record. everyone else
    // falls through to user_options, which load_users only populates for
    // admin viewers — non-admin viewers simply see no role chip for
    // tenant users whose role they can't see.
    //
    // shared by Created By, Additional Contacts, and comment authors on
    // this page — commentRole delegates to it below so all three surfaces
    // agree on who has what role.
    // true when the username is an Initek staff email. accepts both the
    // legacy @initekconsulting.com domain and the post-restructure
    // @fleetintegrations.com domain (S-Corp → C-Corp rename). keep both
    // active until the migration completes and historical comments
    // under the old domain are either rewritten or aged out.
    isStaffEmail(username) {
      if (!username) return false
      const lc = String(username).toLowerCase()
      return lc.endsWith('@initekconsulting.com') || lc.endsWith('@fleetintegrations.com')
    },
    userRole(username) {
      if (!username) return null
      if (this.isStaffEmail(username)) return 'admin'
      // non-email staff usernames (legacy short-username accounts like
      // "jen") still need to resolve as Initek staff. the hardcoded
      // assigned_options list above is the authoritative roster of
      // Initek people who can be assigned tickets — membership there
      // implies admin role. this future-proofs any new short-username
      // staff that land without touching the userRole logic again.
      if ((this.assigned_options || []).some(o => o.value && o.value === username)) return 'admin'
      if (this.user && username === this.user.username) return this.user.role || null
      const match = (this.user_options || []).find(opt => opt.value === username)
      return (match && match.role) ? match.role : null
    },
    userRoleLabel(username) {
      const role = this.userRole(username)
      return role ? roleLabelForCustomer(role) : ''
    },
    userRoleIcon(username) {
      const role = this.userRole(username)
      return role ? roleIconForCustomer(role) : ''
    },
    userRoleChipClass(username) {
      const role = this.userRole(username)
      return role ? ('std-comment-thread-chip--role-' + role) : ''
    },
    // an "external" contact is anyone whose username is set but doesn't
    // resolve to a role — not initek staff, not in assigned_options, not
    // the current user, not in user_options. typical case: an email
    // CC'd into Additional Contacts that has never had an app account.
    // we treat them visually distinct (dashed amber outline + explicit
    // "External" pill) so they don't get mistaken for a customer.
    isExternalContact(username) {
      if (!username) return false
      return !this.userRole(username)
    },
    // class on the OUTER std-contact-chip — drives the chip's border
    // color via the role tag's css variables. for known roles, returns
    // the same role-tag class the inner pill uses (sets --role-tag-color).
    // for external contacts, returns std-contact-chip--external which
    // sets an amber accent + dashed border-style.
    contactChipModifierClass(username) {
      if (this.isExternalContact(username)) return 'std-contact-chip--external'
      return this.userRoleChipClass(username)
    },
    // legacy wrappers so the comment-thread template reads naturally.
    // all three delegate to the username-keyed helpers above — single
    // source of truth for role resolution.
    commentRole(commentItem) {
      if (!commentItem || !commentItem.username) return null
      if (this.isStaffComment(commentItem)) return 'admin'
      if (this.user && commentItem.username === this.user.username) return this.user.role || null
      const match = (this.user_options || []).find(opt => opt.value === commentItem.username)
      return (match && match.role) ? match.role : null
    },
    // comment chips use the customer-facing role variant — Initek staff
    // read as "Support" with a life-ring so we don't expose internal
    // role names to customers on a shared thread. see
    // src/helpers/role_display.js for the single source of truth.
    commentRoleLabel(commentItem) {
      const role = this.commentRole(commentItem)
      return role ? roleLabelForCustomer(role) : ''
    },
    commentRoleIcon(commentItem) {
      const role = this.commentRole(commentItem)
      return role ? roleIconForCustomer(role) : ''
    },
    commentRoleChipClass(commentItem) {
      const role = this.commentRole(commentItem)
      return role ? ('std-comment-thread-chip--role-' + role) : ''
    },
    // compact timestamp for the comment header. day-bucketing upstream
    // means we rarely need the full date, so <24h shows only the
    // clock time. older entries keep the date so "yesterday" vs
    // "earlier this week" rows stay unambiguous at a glance.
    absoluteCommentTime(iso) {
      if (!iso) return ''
      try {
        const m = moment(iso)
        if (!m.isValid()) return ''
        const diffH = Math.abs(moment().diff(m, 'hours'))
        if (diffH < 24) return m.format('h:mm A')
        return m.format('MMM D, h:mm A')
      } catch (_) { return '' }
    },
    // full timestamp used as the hover title on the compact time label,
    // so users can pin down the exact moment without leaving the page.
    // "Mon, Apr 20, 2026 · 2:14:07 PM PDT" reads cleanly in a native
    // browser tooltip and includes the tz abbreviation since moment's
    // default tz was set upstream from the tenant's summary.
    fullCommentTime(iso) {
      if (!iso) return ''
      try {
        const m = moment(iso)
        if (!m.isValid()) return ''
        return m.format('ddd, MMM D, YYYY · h:mm:ss A z')
      } catch (_) { return '' }
    },

    canEditComment(commentItem) {
      return (
        this.effectiveRole === 'admin' ||
        commentItem.username === this.user.username
      )
    },

    canDeleteComment(commentItem) {
      return (
        this.effectiveRole === 'admin' ||
        commentItem.username === this.user.username
      )
    },

    startEdit(commentItem) {
      commentItem.editing = true
    },

    cancelEdit(commentItem) {
      commentItem.editing = false
      commentItem.tempComment = commentItem.comment
    },

    async saveComment(commentItem) {
      if (!this.canEditComment(commentItem)) {
        this.$emit('showAlert', 'Not authorized to update this comment.', 'danger', 3000)
        return
      }
      const updatedText = this.normalizeCommentLines(commentItem.tempComment)
      if (!updatedText) {
        this.$emit('showAlert', 'Comment cannot be empty', 'warning', 2500)
        return
      }

      try {
        this.$emit('showAlert', "Updating support ticket comment...", 'info')
        const response = await update_support_ticket_comment(this.model.tenant_id, { ticket_id: this.model.id,
          comment_id: commentItem.comment_id,
          comment: updatedText })

        if (response.value && response.value.data && response.value.data.status === 'success') {
          this.$emit('showAlert', 'Comment updated successfully!', 'success', 1500)
          commentItem.comment = updatedText
          commentItem.editing = false
          commentItem.updated_at = moment().format()
        } else {
          this.$emit('showAlert', response.value.message || 'Failed to edit comment.', 'danger', 5000)
        }
      } catch (err) {
        this.$emit('showAlert', 'Error editing comment, try again', 'danger', 5000)
      }
    },

    async deleteComment(commentItem) {
      if (!this.canDeleteComment(commentItem)) {
        this.$emit('showAlert', 'Not authorized to delete this comment.', 'danger', 3000)
        return
      }
      if (!confirm('Are you sure you want to delete this comment?')) {
        return
      }

      try {
        this.$emit('showAlert', "Updating support ticket comment...", 'info')
        const response = await delete_support_ticket_comment(this.model.tenant_id, { ticket_id: this.model.id,
          comment_id: commentItem.comment_id })

        if (response.value && response.value.data && response.value.data.status === 'success') {
          this.$emit('showAlert', 'Comment deleted successfully!', 'success', 1500)

          const serverData = response.value.data
          let updatedComments = null
          if (serverData && serverData.Attributes && serverData.Attributes.comments) {
            updatedComments = serverData.Attributes.comments
          }

          if (Array.isArray(updatedComments)) {
            this.model.comments = updatedComments
          } else {
            this.model.comments = this.model.comments.filter(
              (c) => c.comment_id !== commentItem.comment_id
            )
          }
        } else {
          this.$emit('showAlert', response.data.message || 'Failed to delete comment.', 'danger', 5000)
        }
      } catch (err) {
        this.$emit('showAlert', 'Error deleting comment, try again', 'danger', 5000)
      }
    },

    formatDate(dateStr) {
      if (!dateStr) return ''
      return moment(dateStr).format('MM/DD hh:mm A') // e.g., "01/16 03:43 PM"
    },

    isImage(url) {
      if (!url) return false
      const ext = url.split('.').pop().split('?')[0].toLowerCase()
      return ['jpg','jpeg','png','gif','webp','bmp','svg'].includes(ext)
    },

    openLightbox(src, alt) {
      this.lightboxSrc = src
      this.lightboxAlt = alt || ''
      this.$bvModal.show('image-lightbox')
    },

    isLegacyAttachmentPlaceholder(comment) {
      if (!comment) return false
      const text = (comment.comment || '').trim()
      const hasAttachments = comment.attachments && comment.attachments.length > 0
      return hasAttachments && (text === 'initial attachments' || text === 'Email attachments')
    },

    extractFileNameFromUpload(url) {
        const match = url.match(/_(.*)/);
        return match ? match[1] : null;
    },

    getOptionText(options, value) {
      const option = options.find(opt => opt.value === value)
      return option ? option.text : value
    },

    // look up the tenant_options entry and return it if is_vip is true.
    // backend (fleet_service.py login) annotates tenant_options with
    // { is_vip, vip_reason } based on fleet size + multi-tenant group +
    // manual vip_override. mirrors the getTenantVip in support_ticket_list
    // and help_center so the three surfaces share one VIP signal source.
    // gated on effectiveRole so non-admin users (and Muggle preview) never
    // see VIP signaling — matches the FI-1647 "always effectiveRole, never
    // user.role" rule. backend still ships the annotation, but every
    // VIP-driven render path on this page funnels through here so a single
    // bottleneck keeps the chip / classes / row colors all in sync.
    getTenantVip(tenantId) {
      if (this.effectiveRole !== 'admin') return null
      if (!tenantId || !this.user || !this.user.tenant_options) return null
      const opt = this.user.tenant_options.find(o => o.value === tenantId)
      return (opt && opt.is_vip) ? opt : null
    },

    // mirror of support_ticket_list.getPriorityTag so the details page
    // renders the same icon+color+label tag for Priority in readonly
    // view. keep these two in sync if the priority taxonomy changes.
    getPriorityTag(priority) {
      const tags = {
        question:    { label: 'Question',           compactLabel: 'QUESTION',  icon: 'fa-question', variant: 'priority-tag-warning' },
        minor:       { label: 'Minor Bug',          compactLabel: 'MINOR BUG', icon: 'fa-bug',      variant: 'priority-tag-orange-light' },
        urgent:      { label: 'Major Bug / Outage', compactLabel: 'MAJOR BUG', icon: 'fa-bolt',     variant: 'priority-tag-danger' },
        enhancement: { label: 'Enhancement',        compactLabel: 'ENHANCE',   icon: 'fa-wrench',   variant: 'priority-tag-info' },
        feature:     { label: 'New Feature',        compactLabel: 'FEATURE',   icon: 'fa-star',     variant: 'priority-tag-success' },
      }
      return tags[priority] || null
    },

    getEditedAgo(created_at, updated_at) {
      if (updated_at && updated_at !== created_at) {
        return 'Edited ' +  moment(updated_at).from(moment.now())
      }
      else {
        return ''
      }

    },
  },
  watch: {
    user: {
      deep: true,
      handler() {
        this.load_data()
      }
    },
    preparedComments() {
      this.checkCommentOverflow()
    },
    model() {
      this.descriptionExpanded = false
      this.descriptionOverflow = false
      this.internalNotesExpanded = false
      this.internalNotesOverflow = false
      this.checkCommentOverflow()
    },
    selected_target_date() {
      // only sync on an actual edit and only when the parsed date is
      // real. moment(null).format(...) / moment(invalid).format(...)
      // return the literal string "Invalid date" — writing that back
      // clobbers the real estimated_finish_date on the model and makes
      // the ticket render "Invalid date" forever after save. guard both.
      if (!this.isEditing) return
      const m = moment(this.selected_target_date)
      if (!m.isValid()) return
      this.model.estimated_finish_date = m.format('MM-DD-YYYY')
    }
  }
}
</script>

<style>
  /* support ticket details — mirrors the FA-settings / user-profile
     layout: 3-column table rows (label | control | help), steelblue
     section headers with an <hr>, admin-only rows flagged with cream
     + gold-left-pipe on the label cell. mobile ≤900px flips the
     table rows to a stacked grid. */
  .std-root {
    max-width: 1400px;
    /* horizontal gutters live on std-root since the b-card that used
       to supply them is gone. centering + 32px side padding gives the
       content a clear inset from the viewport edges without wrapping
       everything in a bordered box. */
    margin-left: auto;
    margin-right: auto;
    padding: 12px 32px 32px 32px;
  }
  @media (max-width: 720px) {
    .std-root { padding: 12px 16px 24px 16px; }
  }
  /* the main details block is no longer a b-card, so it carries no
     framing of its own — rely on the header's border-bottom to
     separate the title from the form and on section margins below to
     create clear sections without boxing them in. */
  .std-card {
    font-weight: normal;
    margin-top: 0;
  }
  .std-header {
    display: flex;
    align-items: center;
    gap: 12px;
    padding-bottom: 14px;
    border-bottom: 1px solid #e0e0e0;
    margin-bottom: 18px;
  }
  /* push the action cluster (Cancel Ticket / Edit) to the right edge
     so the back-button + title group naturally stays at the left. */
  .std-header-actions { margin-left: auto; }
  .std-title {
    margin: 0;
    font-size: 20px;
    font-weight: 600;
    color: #333;
  }
  .std-header-actions {
    display: flex;
    gap: 6px;
    flex-shrink: 0;
  }
  /* circular back button — sits to the left of the title and drops the
     ticket query params so the help-center parent re-renders the list.
     deliberately quiet visual: thin border + light hover, not a CTA. */
  .std-back-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 34px;
    height: 34px;
    border-radius: 50%;
    border: 1px solid #d4d8dc;
    background: #fff;
    color: #495057;
    font-size: 14px;
    line-height: 1;
    cursor: pointer;
    flex-shrink: 0;
    transition: background-color 0.12s ease, border-color 0.12s ease, color 0.12s ease;
  }
  .std-back-btn:hover,
  .std-back-btn:focus {
    background: var(--fi-brand-tint);
    border-color: var(--fi-rail-accent);
    color: #1f4a73;
    outline: none;
  }
  .std-back-btn .fa { line-height: 1; }

  /* "Agent" export — desktop standalone button. consumes the shared
     admin-cue tokens (--fi-admin-cue-*): today cream fill + warm-gold
     border + deep burnt-orange text, the established "you can see this
     because you're a sys-admin" cue; purple under the new theme. the
     same pairing backs the help_center Article Leaderboard button.
     b-button defaults to variant="secondary" which sets color:#fff at
     equal specificity to ours, so we keep the !important escape hatch. */
  .std-assist-btn.btn {
    color: var(--fi-admin-cue-fg) !important;
    border-color: var(--fi-admin-cue-line) !important;
    background-color: var(--fi-admin-cue-bg) !important;
    transition: background-color 0.12s ease, color 0.12s ease, border-color 0.12s ease;
  }
  .std-assist-btn.btn:hover:not(:disabled),
  .std-assist-btn.btn:focus:not(:disabled) {
    color: var(--fi-admin-cue-fg) !important;
    background-color: var(--fi-admin-cue-bg-hover) !important;
    border-color: var(--fi-admin-cue-fg) !important;
    box-shadow: 0 0 0 0.2rem var(--fi-admin-cue-focus-ring);
  }
  /* "Copied!" success — fill with the cue's solid color so the click
     registers visually before the 2s timeout reverts. */
  .std-assist-btn.btn:active:not(:disabled),
  .std-assist-btn.btn.std-assist-btn--copied {
    color: var(--fi-admin-cue-on-fill) !important;
    background-color: var(--fi-admin-cue-fill) !important;
    border-color: var(--fi-admin-cue-fill) !important;
  }
  .std-assist-btn.btn:disabled {
    color: var(--fi-admin-cue-fg) !important;
    border-color: var(--fi-admin-cue-line) !important;
    background-color: var(--fi-admin-cue-bg) !important;
    opacity: 0.65;
  }

  /* "Agent" export — mobile kebab item. same warm-gold accent so the
     admin-only cue still reads inside the menu, just quieter than the
     standalone button. */
  .std-assist-item .dropdown-item {
    color: var(--fi-admin-cue-fg);
    transition: background-color 0.12s ease, color 0.12s ease;
  }
  .std-assist-item .dropdown-item:hover,
  .std-assist-item .dropdown-item:focus {
    color: var(--fi-admin-cue-fg);
    background-color: var(--fi-admin-cue-bg);
  }
  .std-assist-item.std-assist-item--copied .dropdown-item,
  .std-assist-item.std-assist-item--copied .dropdown-item:hover,
  .std-assist-item.std-assist-item--copied .dropdown-item:focus {
    color: var(--fi-admin-cue-on-fill);
    background-color: var(--fi-admin-cue-fill);
  }
  .std-assist-item .dropdown-item:disabled,
  .std-assist-item .dropdown-item.disabled {
    color: var(--fi-admin-cue-fg);
    background-color: transparent;
    opacity: 0.65;
  }

  /* shared splat icon — sized to match the fa-pencil glyph next to it
     on the Edit button. no margin: the &nbsp; in the template is the
     only separator, mirroring the icon+nbsp pattern Edit / Discard /
     Cancel Ticket all use, so the icon-to-label gap is consistent
     across the action row. */
  .std-assist-splat {
    width: 14px;
    height: 14px;
    vertical-align: -2px;
  }

  /* overflow kebab in the header — quieter than the Edit button so
     destructive / secondary actions don't compete with the primary
     flow. square-ish to match the back button's visual weight. */
  .std-header-overflow .std-header-overflow-toggle {
    width: 34px;
    padding: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }
  .std-header-overflow .std-header-overflow-toggle .fa-ellipsis-v {
    font-size: 14px;
  }
  .std-header-overflow-empty {
    font-size: 12px;
    color: #888;
    padding: 4px 14px;
  }

  /* subject banner — sits above the metadata grid. carries page-level
     weight so it competes with the page header h3 (Support Ticket #N)
     rather than getting lost among the 11px h5 metadata labels. no
     label in view mode; the size + position do the work. shares the
     .std-content-rail class so the steelblue rail threads the
     subject + description + people-fields as one visual group. */
  .std-subject-banner {
    margin: 4px 0 14px 0;
  }
  .std-subject-banner .std-subject-title {
    font-size: 22px;
    font-weight: 600;
    line-height: 1.3;
    color: #1f2937;
    word-break: break-word;
  }
  .std-subject-banner--editing { margin-bottom: 10px; }
  .std-subject-banner--editing > h5 {
    display: block;
    font-size: 11px;
    font-weight: 600;
    color: #888;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    margin: 0 0 4px 0;
  }
  .std-subject-banner .std-subject-input { font-size: 16px; }

  /* shared "content rail" treatment — steelblue leading rail with no
     fill behind. used to mark the meaty content blocks (Subject
     banner, Description body, Created By, Additional Contacts) so the
     rail visually threads the page's primary content together. no
     background — the rail alone carries the cue, keeping the page
     light. left padding clears the 4px rail with a comfortable gap. */
  .std-content-rail {
    box-shadow: inset 4px 0 0 0 var(--fi-rail-accent);
    padding-left: 14px;
  }
  .std-content-rail .std-value--multiline { font-size: 14px; }
  .std-content-rail .std-expand-btn { margin-top: 6px; }

  /* main form — 4-column table, mirrors the original layout. each tr
     is a horizontal band of related fields (Account row, Status row,
     etc); colspan collapses unused cells for full-width fields like
     Subject/Description/Internal Notes. on narrow viewports the whole
     table unfolds into a stack (see media query below) so every cell
     becomes a full-width block — colspans degrade gracefully. */
  .std-table {
    width: 100%;
    border-collapse: collapse;
    table-layout: fixed;
    text-align: left;
    margin-top: 6px;
  }
  .std-table .std-cell {
    vertical-align: top;
    padding: 8px 10px 14px 10px;
    word-break: break-word;
    min-width: 0;
  }
  .std-table .std-cell:first-child { padding-left: 0; }
  .std-table .std-cell:last-child  { padding-right: 0; }
  .std-table .std-cell > h5 {
    display: block;
    font-size: 11px;
    font-weight: 600;
    color: #888;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    margin: 0 0 4px 0;
  }
  .std-field-head {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 6px;
    margin-bottom: 4px;
  }
  .std-field-head > h5 {
    display: block;
    font-size: 11px;
    font-weight: 600;
    color: #888;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    margin-bottom: 0 !important;
  }
  .std-inline-icon-btn {
    border: 0;
    background: transparent;
    color: #6b7280;
    padding: 0;
    line-height: 1;
    cursor: pointer;
  }
  .std-inline-icon-btn:hover,
  .std-inline-icon-btn:focus {
    color: #1f4a73;
    outline: none;
  }

  /* admin-only triage row — cream fill across all cells in the row,
     gold left-pipe anchored to the first cell. matches the FA-settings
     / user-profile convention (#f0a957 + #fff8ef). */
  .std-row--admin > .std-cell {
    background: var(--fi-admin-cue-bg);
    padding-top: 10px;
    padding-bottom: 14px;
  }
  .std-row--admin > .std-cell:first-child {
    border-left: 3px solid var(--fi-admin-cue-line);
    padding-left: 10px;
  }

  .std-value {
    color: #333;
    word-break: break-word;
    font-size: 14px;
  }
  .std-value--strong { font-weight: 600; font-size: 16px; }

  /* empty-state cell. the structural slot stays put across view/edit
     modes (so toggling Edit never shifts the layout); the label fades
     to a lighter weight and we drop a thin baseline rule where the
     value would have been, so the cell reads as "field exists, no
     value yet" without claiming any visual weight. applies only in
     view mode (once an input is rendered, the cell is no longer empty
     in any meaningful sense). */
  .std-cell--empty > h5 { color: #b8bcc2; }
  .std-cell--empty::after {
    content: '';
    display: block;
    height: 1px;
    width: 24px;
    background: #e3e6eb;
    margin-top: 6px;
  }

  /* the Account cell shows an inline VIP pill to the right of the tenant
     name when the backend-annotated tenant_options entry carries is_vip.
     matches the list view's gold palette (#d4a017 / #fff4d1 / #7a5400) so
     the signal reads as one thing across list + detail. sized to mirror
     the navbar .profile-role-tag / .std-comment-thread-chip so it sits
     naturally alongside other chips elsewhere on the page. */
  .std-account-wrap {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
  }
  .std-account-wrap > .std-value,
  .std-account-wrap > .custom-select {
    /* let the value/select size naturally instead of growing to the flex line */
    flex: 0 1 auto;
  }
  /* the wrapper is the positioning context for both the chip (which
     clips the shimmer via overflow: hidden) and the sparkle (which pokes
     outside the chip's bounding box at the top-right corner — see
     std-vip-wrap::after). keeping the tooltip/cursor on the wrapper so
     the sparkle region also triggers the title popup. */
  .std-vip-wrap {
    position: relative;
    display: inline-flex;
    cursor: help;
  }
  .std-vip-chip {
    display: inline-flex;
    align-items: center;
    height: 20px;
    padding: 2px 8px;
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.3px;
    color: #7a5400;
    background: #fff4d1;
    border: 1px solid #d4a017;
    border-radius: 4px;
    white-space: nowrap;
    line-height: 1;
    position: relative;
    overflow: hidden;
  }
  /* subtle gold-tinted shimmer sweeping across the VIP pill every few
     seconds so the chip pulls the eye without being noisy. 4s cycle —
     slowed 25% from the original 3.2s per design feedback. the gradient
     sweeps through the first ~55% of the cycle, then rests off-stage
     for the remainder so it doesn't read as continuous motion.
     pointer-events none keeps the underlying tooltip intact. */
  .std-vip-chip::before {
    content: '';
    position: absolute;
    inset: 0;
    pointer-events: none;
    background: linear-gradient(
      100deg,
      rgba(255, 255, 255, 0) 35%,
      rgba(255, 255, 255, 0.75) 50%,
      rgba(255, 255, 255, 0) 65%
    );
    transform: translateX(-110%);
    animation: std-vip-shimmer 4s ease-in-out infinite;
  }
  /* sparkle on the top-RIGHT corner of the VIP chip. fixed position
     regardless of which side of the account the chip sits on. timed
     to the shimmer's downbeat — pops in just after the bright sweep
     exits, so it reads as "the sweep left a sparkle behind" rather
     than landing on top of the sweep itself. lives on the wrapper
     (not the chip) so the chip's overflow: hidden doesn't clip it. */
  .std-vip-wrap::after {
    content: '\2726'; /* ✦ four-point star — crisper than the emoji ✨ which varies by platform */
    position: absolute;
    top: -7px;
    right: -5px;
    font-size: 11px;
    line-height: 1;
    color: #d4a017;
    text-shadow: 0 0 3px rgba(255, 237, 135, 0.9);
    opacity: 0;
    transform: scale(0) rotate(-15deg);
    transform-origin: center;
    animation: std-vip-sparkle 4s ease-in-out infinite;
    pointer-events: none;
  }
  @keyframes std-vip-shimmer {
    0%   { transform: translateX(-110%); }
    55%  { transform: translateX(110%); }
    100% { transform: translateX(110%); }
  }
  /* peak pushed from 25% → 40% of the cycle so the sparkle reads AFTER
     the shimmer has swept past, not during. */
  @keyframes std-vip-sparkle {
    0%, 30%     { opacity: 0; transform: scale(0) rotate(-15deg); }
    40%         { opacity: 1; transform: scale(1.25) rotate(10deg); }
    50%         { opacity: 0.85; transform: scale(1) rotate(25deg); }
    65%, 100%   { opacity: 0; transform: scale(0) rotate(40deg); }
  }
  @media (prefers-reduced-motion: reduce) {
    .std-vip-chip::before,
    .std-vip-wrap::after { animation: none; }
    .std-vip-wrap::after { opacity: 0; }
  }

  /* label helper text shown only in edit mode ("(comma separated emails)")
     stays readable but visually muted so it doesn't compete with the
     primary field heading. */
  .std-label-hint {
    font-weight: 400;
    color: #888;
    font-size: 12px;
  }

  /* Additional Contacts renders as individual pills in readonly mode —
     the old raw "a@x.com,b@y.com" string was hard to parse. each chip
     shows the resolved name (via displayUserName) with the raw email as
     a hover tooltip, mirroring how Created By / comment authors already
     prefer name over email on this page. */
  .std-person-display {
    display: inline-flex;
    align-items: center;
    gap: 8px;
  }
  .std-team-avatar {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    object-fit: cover;
    border: 2px solid var(--fi-brand-tint-border);
    box-shadow: 0 1px 3px rgba(70, 130, 180, 0.12);
    flex: 0 0 auto;
  }
  .std-contact-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
  }
  /* outer chip — transparent fill with the border colored by the role
     pill's *background* tint (--role-tag-bg). the soft tint reads as a
     quiet outline that lets the inner pill carry the visual weight,
     while still keying the chip to the contact's role at a glance.
     std-comment-thread-chip--role-X (applied via contactChipModifierClass)
     only sets css variables, so applying it to the outer chip is purely
     a scope-handoff — nothing else inherits. external contacts (no
     resolved role) swap in std-contact-chip--external for a dashed
     amber border so they don't get mistaken for a known customer. */
  .std-contact-chip {
    display: inline-flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px;
    padding: 3px 4px 3px 10px;
    max-width: 100%;
    font-size: 13px;
    color: #2c3e50;
    background: transparent;
    border: 1px solid var(--role-tag-bg, #cbd5e1);
    border-radius: 12px;
    cursor: help;
    line-height: 1.3;
  }
  /* external contact — dashed amber border + tonally-matched "External"
     pill via std-contact-chip-role--external below. amber chosen to be
     clearly distinct from existing role accents (admin gold, customer-
     admin blue, customer grey, etc) so the at-a-glance signal is "not
     one of ours, don't assume role". border-color jumps to the strong
     accent (--role-tag-color) instead of the soft bg tint so the
     dashed outline stays legible. */
  .std-contact-chip--external {
    --role-tag-color: #b45309;
    border-color: var(--role-tag-color);
    border-style: dashed;
  }
  /* explicit External pill — amber palette matching the dashed border,
     with fa-user-o (outline user) to read as "user-shaped, but not in
     our system". sits in the same role-pill slot as the regular role
     pill so the chip layout stays consistent. */
  .std-contact-chip-role--external {
    --role-tag-color: #b45309;
    --role-tag-bg: #fff7ed;
    --role-tag-fg: #7c2d12;
  }
  /* name text keeps its own nowrap + ellipsis so it doesn't break mid-word.
     if the name + role pill can't fit on one line, flex-wrap on the parent
     chip drops the role pill onto a second line. if the name ALONE is
     wider than the cell, it ellipsis-truncates instead of forcing the
     chip to overflow its table cell (the overflow case showed up in a
     ~1200px viewport where the 4-col `.std-row` squeezed Created By down
     and "Roxanne Gillis FLEET ADMIN" crashed into Estimated Dev). */
  .std-contact-chip-name {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    min-width: 0;
    max-width: 100%;
  }
  /* inner role pill sits next to the name inside the contact chip. reuses
     the .std-comment-thread-chip palette (role-admin / customer-admin /
     driver etc) so the same role reads the same wherever it appears on
     this page. slightly smaller height than the comment-thread variant
     so it sits inside the outer 13px pill without crowding. */
  .std-contact-chip-role {
    height: 18px;
    font-size: 9px;
    padding: 1px 6px;
    letter-spacing: 0.2px;
  }
  .std-value--multiline { white-space: pre-wrap; word-break: break-word; }
  /* description / internal notes in view mode are rendered via v-html
     after linkify — style the inner <a>s so urls read as links and
     don't blend into the body copy. word-break so long urls wrap. */
  .std-value--linkified a {
    color: #1f4a73;
    text-decoration: underline;
    word-break: break-all;
  }
  .std-value--linkified a:hover,
  .std-value--linkified a:focus { color: #0d2f4f; text-decoration: underline; }

  /* dev-ticket value in view mode — when it matches the Jira key
     format (FI-1234) the value becomes a clickable link to the
     Atlassian issue. small trailing icon signals external nav. */
  .std-dev-ticket-link {
    color: #1f4a73;
    text-decoration: none;
    font-weight: 500;
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }
  .std-dev-ticket-link:hover,
  .std-dev-ticket-link:focus { color: #0d2f4f; text-decoration: underline; }
  .std-dev-ticket-link .fa-external-link { font-size: 11px; color: #8a8f94; }
  .std-value--inline { display: inline; }

  .std-status-actions {
    margin-top: 6px;
    display: flex;
    gap: 6px;
    flex-wrap: wrap;
  }

  .std-hours-row {
    display: flex;
    align-items: center;
    gap: 6px;
  }
  .std-hours-input {
    width: 70px !important;
    max-width: 70px;
    flex: 0 0 70px;
  }
  .std-hours-unit {
    font-size: 13px;
    color: #555;
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }
  .std-hours-cost {
    color: #888;
    font-size: 12px;
  }

  .std-date-picker { max-width: 160px; }

  .std-edit-footer {
    display: flex;
    gap: 8px;
    margin-top: 18px;
    padding-top: 14px;
    border-top: 1px solid #e0e0e0;
  }

  /* secondary sections below the main form (Attachments, Comments,
     History) share the same header treatment and density as the form
     itself so the whole page reads as one document, not three cards
     glued together. */
  /* secondary sections (Attachments, Comments, History) flow as flat
     blocks of a single document — no box, no shadow. the section
     title + border-bottom does all the visual separation work.
     margin-top gives enough air between blocks so they don't read as
     one big wall of content. */
  .std-section-card {
    font-weight: normal;
    margin-top: 32px;
  }
  /* headless variant — used by the read-only Attachments section, where
     the header is dropped and the button + grid sit directly under the
     description block. tighter top margin so there's no orphaned gap
     where the missing header used to live. */
  .std-section-card--headless {
    margin-top: 10px;
  }
  .std-section-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;
    padding-bottom: 8px;
    border-bottom: 1px solid #e0e0e0;
  }
  .std-section-header--clickable {
    cursor: pointer;
    user-select: none;
  }
  .std-section-header--clickable:hover .std-section-title {
    color: var(--fi-brand);
  }
  /* admin-only section header: cream row + gold rail, matching the
     triage-band treatment elsewhere in this page so admin-gated
     content reads consistently. */
  .std-section-header--admin {
    background: var(--fi-admin-cue-bg);
    border-left: 3px solid var(--fi-admin-cue-line);
    border-bottom-color: var(--fi-admin-cue-line);
    padding: 10px 12px;
    border-radius: 0 4px 4px 0;
  }
  .std-section-header--admin .std-section-title {
    color: var(--fi-admin-cue-fg);
  }
  .std-section-header--admin .fa {
    color: var(--fi-admin-cue-fg);
  }
  .std-section-header--admin.std-section-header--clickable:hover .std-section-title {
    color: var(--fi-admin-cue-fg);
  }
  .std-section-title {
    margin: 0;
    font-size: 16px;
    font-weight: 600;
    color: #333;
  }
  /* section header text block — when a section needs both a title and
     a subtitle line (e.g. Comments' count), they stack here. the
     wrapping flex on .std-section-header keeps action chrome on the
     right aligned to the full text block, not just the title. */
  .std-section-header-text {
    display: flex;
    flex-direction: column;
    min-width: 0;
  }
  .std-section-subtitle {
    margin-top: 2px;
    font-size: 12px;
    color: #6c757d;
  }

  /* new comment composer: label on top, textarea full-width beneath,
     attachment picker + post button sharing a row below. avoids the
     1/3/7/1 grid that crushed "New Comment:" to one letter per line
     on mid-width viewports. */
  .std-comment-composer {
    margin-bottom: 16px;
  }
  .std-comment-composer-body {
    position: relative;
  }
  .std-comment-composer-overlay {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    background: rgba(255, 255, 255, 0.82);
    border-radius: 4px;
    z-index: 1;
    font-size: 13px;
    color: #555;
    pointer-events: all;
  }
  .std-comment-composer--posting .std-comment-textarea {
    opacity: 0.55;
  }
  .std-comment-post-spinner {
    margin-right: 4px;
    vertical-align: -2px;
  }
  .std-comment-composer-label {
    display: block;
    font-size: 11px;
    font-weight: 600;
    color: #888;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    margin-bottom: 3px;
  }
  .std-comment-textarea {
    width: 100%;
    font-size: 14px;
  }
  .std-comment-composer-row {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 8px;
    flex-wrap: wrap;
  }
  /* Post sits at the opposite end of the row from Add Attachment(s);
     using margin-left: auto pins it right regardless of how many
     buttons are between (or whether Posting… replaces Post). */
  .std-comment-post {
    margin-left: auto;
  }

  /* staged comment attachment chips — render under the composer row
     after the user picks files but before Post. dismissible via the
     × on each chip. visual language matches the muted callout palette
     (soft gray border + light gray fill), not a loud primary color. */
  .std-comment-attachment-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: 8px;
  }
  .std-comment-attachment-chip {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 3px 4px 3px 10px;
    background: #f1f3f5;
    border: 1px solid #d4d8dc;
    border-radius: 14px;
    font-size: 12px;
    color: #495057;
    max-width: 240px;
  }
  .std-comment-attachment-chip-name {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .std-comment-attachment-chip-remove {
    border: none;
    background: transparent;
    color: #6c757d;
    font-size: 16px;
    line-height: 1;
    padding: 0 4px;
    cursor: pointer;
    border-radius: 50%;
  }
  .std-comment-attachment-chip-remove:hover {
    background: #dee2e6;
    color: #343a40;
  }

  /* mobile: the 4-col desktop table unfolds into a per-<tr> 2-col grid.
     previously we used `display: contents` on each tr to lift cells
     into a shared tbody grid, but iOS Safari doesn't reliably
     propagate grid layout through display:contents — rows collapsed
     to a single column on iPhone. per-tr grids work everywhere and
     keep the logical grouping of each row (Account row, Status row,
     etc) intact. cells with colspan=4 (Subject, Description) or
     --full-mobile (Dev Ticket, Internal Notes) still span the row
     via grid-column: 1/-1; wide colspan cells (Additional Contacts)
     fall back to normal half-width flow. */
  @media (max-width: 720px) {
    .std-table,
    .std-table > tbody,
    .std-table > tbody > tr {
      display: block;
    }
    .std-table > tbody > tr {
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      column-gap: 14px;
      row-gap: 10px;
      margin-bottom: 4px;
    }
    .std-table > tbody > tr > .std-cell {
      display: block;
      width: auto !important;
      padding: 2px 0 6px 0 !important;
    }
    .std-table > tbody > tr > .std-cell[colspan="4"],
    .std-table > tbody > tr > .std-cell--full-mobile {
      grid-column: 1 / -1;
    }
    .std-table > tbody > tr > .std-cell[colspan="3"]:not(.std-cell--full-mobile) {
      grid-column: auto;
    }
    /* admin row: cream + gold pipe apply to the <tr> itself (it's now
       display:grid, not display:contents, so backgrounds paint here).
       larger row-gap between the two stacked fields (Dev Ticket /
       Internal Notes) so the cream block feels like a section, not
       two adjacent lines crammed together. */
    .std-row--admin {
      background: var(--fi-admin-cue-bg);
      border-left: 3px solid var(--fi-admin-cue-line);
      padding: 10px 12px 12px 12px;
      border-radius: 0 4px 4px 0;
      margin: 6px 0 14px;
      row-gap: 14px;
    }
    .std-row--admin > .std-cell,
    .std-row--admin > .std-cell:first-child {
      background: transparent;
      border-left: none;
      padding: 0 !important;
    }
    .std-title { font-size: 18px; }
  }
  /* very narrow phones drop to single column so nothing gets cramped. */
  @media (max-width: 400px) {
    .std-table > tbody > tr { grid-template-columns: 1fr; }
  }

  /* priority tag — ported from support_ticket_list so readonly Priority
     renders the same icon+pill as the list column. single source of
     truth lives in the list; mirror any palette/icon changes here. */
  .std-root .priority-tag {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    /* 5px horizontal matches the list's icon-only variant so the pill
       reads at the same visual density as the admin list column. */
    padding: 3px 5px;
    font-size: 11px;
    font-weight: 600;
    /* line-height: 1 across the container + both children so the icon
       and label boxes match their glyph heights. `align-items: center`
       then lines up the visible content, not just the differently-tall
       inherited line boxes. */
    line-height: 1;
    border-radius: 4px;
    border: 1px solid;
    white-space: nowrap;
    text-transform: uppercase;
    letter-spacing: 0.3px;
  }
  .std-root .priority-tag--labeled { cursor: default; }
  .std-root .priority-tag .fa {
    font-size: 12px;
    line-height: 1;
    width: 14px;
    text-align: center;
  }
  .std-root .priority-tag-label {
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.3px;
    line-height: 1;
  }
  .std-root .priority-tag-info    { background: #eaf3fb; border-color: #4a7ba7; color: #1f4a73; }
  .std-root .priority-tag-warning { background: #fff4d1; border-color: #b58100; color: #7a5400; }
  .std-root .priority-tag-danger  { background: #fbe3e5; border-color: #dc3545; color: #dc3545; }
  .std-root .priority-tag-success { background: #edf7ee; border-color: #3a8144; color: #1f5727; }
  .std-root .priority-tag-orange-light { background: #ffe8d4; border-color: #ff6d00; color: #ff6d00; }
  .std-root .priority-tag-muted { opacity: 0.45; }
  .std-root .priority-tag--strike .fa-bolt {
    animation: std-priority-bolt-strike 2s infinite ease-out;
    transform-origin: center center;
  }
  @keyframes std-priority-bolt-strike {
    0%, 14%, 100% { transform: scale(1);   filter: brightness(1); }
    4%            { transform: scale(1.25); filter: brightness(1.7); }
    8%            { transform: scale(1);   filter: brightness(1); }
    10%           { transform: scale(1.2); filter: brightness(1.6); }
    13%           { transform: scale(1);   filter: brightness(1); }
  }

  /* primary-action outline buttons (Edit, Reopen) — scope the bootstrap
     variant under .std-root so the action token drives both themes:
     bootstrap blue today, azure brand under the new theme. */
  .std-root .btn-outline-primary { color: var(--fi-action-primary); border-color: var(--fi-action-primary); }
  .std-root .btn-outline-primary:hover, .std-root .btn-outline-primary:focus { color: #fff; background-color: var(--fi-action-primary); border-color: var(--fi-action-primary); }
  .std-root .btn-outline-primary:focus { box-shadow: 0 0 0 0.2rem var(--fi-action-primary-ring); }

  /* history table — canonical b-table pattern. see
     memory/standard_table_pattern.md. duplicated from
     support_ticket_list.vue (their styles are scoped, so we can't
     reach them here). keep these two in sync until/unless we move
     the pattern to a shared stylesheet. */
  .std-history-wrap { margin-top: 6px; }
  /* history rows often hold multi-line diffs in "Changes Made" while
     the timestamp + author cells are single-line. top-align so short
     cells anchor to the row's top rather than floating in the middle
     of a tall multi-line cell. id-scoped so it beats the inherited
     vertical-align: middle rule from .stl-root above. */
  #history_table.b-table td,
  #history_table.b-table th { vertical-align: top !important; }
  .stl-root th[aria-sort] {
    background-image: none !important;
    padding-right: 0.75rem !important;
    white-space: nowrap;
  }
  .stl-root th[aria-sort]::before { display: none !important; }
  .stl-root th[aria-sort]::after {
    display: inline-block !important;
    margin-left: 4px;
    font-size: 10px;
    color: #999;
    vertical-align: middle;
    float: none !important;
    width: auto !important;
    height: auto !important;
    opacity: 1 !important;
  }
  .stl-root th[aria-sort="none"]::after       { content: "\2195" !important; }
  .stl-root th[aria-sort="ascending"]::after  { content: "\25B2" !important; color: var(--fi-rail-accent); }
  .stl-root th[aria-sort="descending"]::after { content: "\25BC" !important; color: var(--fi-rail-accent); }

  .stl-root .table.b-table {
    font-size: 13px;
    border-collapse: collapse;
  }
  .stl-root .table.b-table th,
  .stl-root .table.b-table td {
    border-left: none !important;
    border-right: none !important;
    border-bottom: 1px solid #e0e0e0;
    padding: 3px 8px !important;
    vertical-align: middle !important;
    text-align: left !important;
  }
  .stl-root .table.b-table th:first-child,
  .stl-root .table.b-table td:first-child { padding-left: 12px !important; }
  .stl-root .table.b-table th:last-child,
  .stl-root .table.b-table td:last-child  { padding-right: 12px !important; }
  .stl-root .table.b-table thead th {
    background: #f8f9fa;
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 0.3px;
    color: #555;
    position: sticky;
    top: 0;
    z-index: 2;
    box-shadow: inset 0 -2px 0 0 #ccc;
  }
  .stl-root .table.b-table tbody tr:hover td { background-color: var(--fi-brand-tint); }

  .support-ticket-description-cell {
    max-width: 0;
  }
  .support-ticket-description-wrap {
    width: 100%;
    max-width: 100%;
    min-width: 0;
  }
  .support-ticket-description,
  .support-ticket-description-wrap textarea {
    width: 100% !important;
    max-width: 100% !important;
    box-sizing: border-box;
  }
  .gap-2 {
    gap: 0.5rem;
  }
  /* skeleton loader — gray placeholder bars with a left-to-right
     shimmer sweep. used while load_data() runs so the user sees a
     ticket-shaped silhouette instead of a blank page. shape variants
     map to the real elements they stand in for: title bar, label,
     value line, multi-line block. */
  .skeleton {
    display: inline-block;
    background: linear-gradient(90deg, #ececec 0%, #f5f5f5 50%, #ececec 100%);
    background-size: 200% 100%;
    border-radius: 4px;
    animation: std-skeleton-shimmer 1.4s ease-in-out infinite;
    line-height: 1;
  }
  @keyframes std-skeleton-shimmer {
    0%   { background-position: 200% 0; }
    100% { background-position: -200% 0; }
  }
  /* skeleton primitives — sized to match the real elements they sub
     for. titles/buttons match approximate widths so the page doesn't
     jump when real content lands. */
  .skeleton-title         { height: 22px; width: 220px; }
  .skeleton-label         { display: block; height: 11px; width: 70px; margin-bottom: 6px; border-radius: 3px; }
  .skeleton-value         { display: block; height: 14px; width: 70%; }
  .skeleton-value--wide   { width: 90%; }
  .skeleton-value--narrow { width: 50%; }
  .skeleton-subject       { display: block; height: 18px; width: 70%; }
  .skeleton-block         { display: block; height: 84px; width: 100%; border-radius: 6px; }
  .skeleton-block--short  { height: 44px; }
  .skeleton-block--input  { height: 60px; border-radius: 4px; }
  .skeleton-tag           { display: inline-block; width: 96px; height: 22px; border-radius: 4px; }
  .skeleton-btn           { display: inline-block; width: 100px; height: 28px; border-radius: 4px; }
  .skeleton-btn--narrow   { width: 60px; }
  .skeleton-section-title { display: block; height: 16px; width: 140px; }
  .skeleton-subtitle      { display: block; height: 11px; width: 110px; margin-top: 6px; border-radius: 3px; }
  .skeleton-value--xnarrow { width: 30%; }
  .skeleton-group-header  { display: block; height: 11px; width: 56px; border-radius: 3px; margin: 10px 0 8px; }
  .skeleton-puck          { display: inline-block; width: 32px; height: 32px; border-radius: 50%; flex-shrink: 0; }
  .std-skeleton-comment-card { margin-top: 14px; }
  .std-skeleton-comment-item {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 14px 8px;
    border: 1px solid #edf0f3;
    border-radius: 8px 8px 0 0;
    border-bottom: none;
  }
  .std-skeleton-comment-item-meta {
    display: flex;
    flex-direction: column;
    gap: 6px;
    flex: 1 1 auto;
    min-width: 0;
  }
  /* stitched-together ghost card: item above (rounded top) + body
     below (rounded bottom) read as one card while the shimmer rolls
     across both halves in sync. */
  .std-skeleton-comment-card .skeleton-block--short {
    border-top: none;
    border-radius: 0 0 8px 8px;
    height: 48px;
  }

  .std-skeleton-header-actions {
    display: flex;
    gap: 6px;
  }

  /* subject banner placeholder sits as a sibling above the admin
     band + metadata grid, matching the rendered subject's position
     above the table. picks up the same 6px top margin the grid used
     to carry so spacing under the header is unchanged. */
  .std-skeleton-subject-row {
    margin-top: 6px;
    padding: 4px 0;
  }

  .std-skeleton-grid {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 14px 18px;
    margin-top: 6px;
  }
  .std-skeleton-cell { min-width: 0; padding: 4px 0; }
  .std-skeleton-cell--full { grid-column: 1 / -1; }
  .std-skeleton-cell--span2 { grid-column: span 2; }
  .std-skeleton-cell--span3 { grid-column: span 3; }
  /* empty-state skeleton: matches the rendered std-cell--empty shape
     (label only, thin hairline rule below) so cells that are commonly
     empty post-load don't reflow when content paints. the value
     placeholder is replaced with a 24px hairline rule that mirrors
     the real ::after used on std-cell--empty. */
  .std-skeleton-cell--empty .skeleton-label { opacity: 0.5; }
  .std-skeleton-cell--empty::after {
    content: '';
    display: block;
    height: 1px;
    width: 24px;
    background: #e3e6eb;
    margin-top: 6px;
  }
  @media (max-width: 720px) {
    .std-skeleton-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
    .std-skeleton-cell--span3 { grid-column: 1 / -1; }
  }
  @media (max-width: 400px) {
    .std-skeleton-grid { grid-template-columns: 1fr; }
  }

  /* admin triage band placeholder. cream + gold pipe matches the real
     admin row treatment, and the inner 4-col grid lets Dev Ticket
     (1 col) sit beside Internal Notes (3 cols) just like the rendered
     row, so admins don't see a layout shift when the actual triage row
     paints in. stacks on narrow viewports via the same breakpoints as
     the main metadata grid. */
  .std-skeleton-admin {
    background: var(--fi-admin-cue-bg);
    border-left: 3px solid var(--fi-admin-cue-line);
    padding: 10px 14px 12px 14px;
    border-radius: 0 4px 4px 0;
    margin: 6px 0 14px;
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 14px 18px;
  }
  .std-skeleton-admin .std-skeleton-cell { padding: 4px 0; }
  .std-skeleton-admin-notes { grid-column: span 3; }
  @media (max-width: 720px) {
    .std-skeleton-admin { grid-template-columns: repeat(2, minmax(0, 1fr)); }
    .std-skeleton-admin-notes { grid-column: 1 / -1; }
  }
  @media (max-width: 400px) {
    .std-skeleton-admin { grid-template-columns: 1fr; }
  }

  .std-skeleton-comment-row {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 8px;
  }
  .std-skeleton-post { margin-left: auto; width: 110px; }
  /* attachments-row skeleton — mirrors the post-load read-only layout
     where "No attachments yet." sits inline next to the Add Attachment
     button on the same flex line. dim hint is sized to roughly match
     the real text width. */
  .std-skeleton-attachments-row {
    display: flex;
    align-items: center;
    gap: 10px;
  }
  .skeleton-empty-hint { display: inline-block; height: 11px; width: 130px; border-radius: 3px; }
  /* skeleton subject inside the content rail keeps its width but loses
     the inline-block default margin so the rail's box-shadow draws
     flush with the placeholder's height. */
  .std-content-rail .skeleton-subject { vertical-align: top; }

  /* comments thread — message-board style that echoes the notification
     list's design language. each comment is a flat card with a pastel
     brand-tinted puck avatar (Initek staff show a profile photo instead;
     role is carried by the chip, not the avatar), name + optional
     Staff/You chips + relative timestamp,
     then prose body, then optional attachment strip. own comments
     pick up a steelblue leading rail (inset box-shadow, no layout
     shift vs other cards) instead of a full-card background tint,
     matching the notification-list unread rail and the admin-only-row
     gold pipe used elsewhere in the ticket system. */
  .std-comment-thread {
    display: flex;
    flex-direction: column;
    gap: 4px;
    margin-top: 4px;
  }
  .std-comment-group + .std-comment-group { margin-top: 12px; }
  .std-comment-group-header {
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    font-weight: 700;
    color: #8a8a8a;
    padding: 0 2px;
    margin: 6px 0 6px;
  }
  .std-comment-thread-item {
    position: relative;
    border: 1px solid #edf0f3;
    border-radius: 8px;
    padding: 10px 14px 12px;
    background: #ffffff;
    transition: box-shadow 0.12s ease, border-color 0.12s ease;
    margin-bottom: 6px;
  }
  .std-comment-thread-item:last-child { margin-bottom: 0; }
  .std-comment-thread-head {
    display: flex;
    align-items: center;
    gap: 10px;
  }
  /* avatar — matches the profile-dropdown avatar (.profile-dd-avatar in
     navbar.vue): pale-blue filled circle, 2px pastel border, steelblue
     initial text, subtle steelblue-tinted drop shadow. sized at 40px
     (close to the popup's 46px) so the name + full-size role chip
     beside it read in the same visual register as the profile popup.
     the popup's steelblue-only palette gets a role analog so staff
     comments still read as staff at a glance: steelblue family for
     customers (exact popup colors), amber family for Initek staff.
     same shape, typography, and shadow so the two surfaces read as
     the same visual system. */
  /* single avatar treatment across all authors — role is carried by the
     chip next to the name, not by avatar color. palette stays steelblue
     so it pairs with the page's overall accent color. future: when users
     can upload a photo, the initial-fallback version renders the same
     way regardless of role. */
  .std-comment-thread-avatar {
    flex: 0 0 auto;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    border: 2px solid var(--fi-brand-tint-border);
    background: var(--fi-brand-tint);
    color: var(--fi-rail-accent);
    font-size: 15px;
    font-weight: 700;
    letter-spacing: 0.5px;
    padding-top: 1px;
    line-height: 1;
    text-transform: uppercase;
    user-select: none;
    box-sizing: border-box;
    box-shadow:
      0 2px 5px rgba(70, 130, 180, 0.16),
      0 1px 2px rgba(70, 130, 180, 0.08);
  }
  .std-comment-thread-avatar--photo {
    padding: 0;
    background: transparent;
    object-fit: cover;
  }
  .std-comment-thread-meta {
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    min-width: 0;
  }
  .std-comment-thread-name-row {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
    line-height: 1.1;
  }
  .std-comment-thread-name {
    font-weight: 600;
    font-size: 15px;
    color: #222;
    line-height: 1.1;
  }
  /* role/ownership chip — identical shape + size to the profile popup's
     .profile-role-tag so a given role reads the same across both
     surfaces (icon + uppercase label, 1px pastel border, 4px radius,
     10px text). variant classes set the color pair via CSS custom
     properties to keep the base rule slim. */
  .std-comment-thread-chip {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 2px 8px;
    min-height: 20px;
    font-size: 10px;
    line-height: 12px;
    font-weight: 700;
    letter-spacing: 0.3px;
    text-transform: uppercase;
    border-radius: 4px;
    border: 1px solid var(--role-tag-color, #6c757d);
    background: var(--role-tag-bg, #e9ecef);
    color: var(--role-tag-fg, #333);
    white-space: nowrap;
  }
  .std-comment-thread-chip .fa {
    font-size: 10px;
    line-height: 1;
  }

  /* icon-only variant — mirrors .priority-tag--icon-only in
     support_ticket_list.vue. label is hidden (moved to title + aria-label)
     so the chip collapses to a compact square around the glyph. keeps the
     same role palette via --role-X modifiers so the color still carries
     the meaning without the word. */
  .std-comment-thread-chip--icon-only {
    gap: 0;
    padding: 3px 5px;
    min-height: 20px;
    cursor: help;
  }
  .std-comment-thread-chip--icon-only .fa {
    font-size: 13px;
    width: 14px;
    text-align: center;
  }

  /* You — steelblue pair, aligned with the own-comment rail. */
  .std-comment-thread-chip--you {
    --role-tag-color: var(--fi-rail-accent);
    --role-tag-bg: var(--fi-brand-tint);
    --role-tag-fg: #1f4a73;
  }

  /* role chip palette — mirrors navbar.vue's .profile-role-tag-*
     colors so the same role reads the same in both surfaces. sys admin
     uses --fi-role-admin-* (rich gold in Original, purple in New — the
     role-identity treatment, same token as the navbar pill); customer
     care uses --fi-role-care-* (purple in Original, rose in New) so it
     never collides with the admin purple. the rest are fixed-vocab. */
  .std-comment-thread-chip--role-admin              { --role-tag-color: var(--fi-role-admin-line); --role-tag-bg: var(--fi-role-admin-bg); --role-tag-fg: var(--fi-role-admin-fg); }
  .std-comment-thread-chip--role-customer-admin     { --role-tag-color: #1e7eeb; --role-tag-bg: #e7f1ff; --role-tag-fg: #0b3d91; }
  .std-comment-thread-chip--role-customer           { --role-tag-color: #6c757d; --role-tag-bg: #eef0f3; --role-tag-fg: #3a3f45; }
  .std-comment-thread-chip--role-booking-customer   { --role-tag-color: #0aa69a; --role-tag-bg: #d9f5f1; --role-tag-fg: #0a6e67; }
  .std-comment-thread-chip--role-driver             { --role-tag-color: #28a745; --role-tag-bg: #e9f6ee; --role-tag-fg: #155724; }
  .std-comment-thread-chip--role-customer-care      { --role-tag-color: var(--fi-role-care-line); --role-tag-bg: var(--fi-role-care-bg); --role-tag-fg: var(--fi-role-care-fg); }
  .std-comment-thread-chip--role-manager            { --role-tag-color: #3d4fb0; --role-tag-bg: #e4e8f8; --role-tag-fg: #27336d; }
  .std-comment-thread-chip--role-dispatch           { --role-tag-color: #c48b00; --role-tag-bg: #fff1d1; --role-tag-fg: #6b4b00; }
  .std-comment-thread-chip--role-garage             { --role-tag-color: #5a6473; --role-tag-bg: #e6e9ee; --role-tag-fg: #2d3440; }
  .std-comment-thread-chip--role-wash-bay           { --role-tag-color: #17a2b8; --role-tag-bg: #dff5f8; --role-tag-fg: #0b6373; }
  .std-comment-thread-time {
    font-size: 12.5px;
    color: #8a8f94;
    margin-top: 3px;
    font-variant-numeric: tabular-nums;
    /* help cursor hints at the native tooltip that carries the full
       timestamp (including the exact date, seconds, and tz). */
    cursor: help;
  }
  .std-comment-thread-actions {
    display: flex;
    gap: 4px;
    flex-shrink: 0;
    transition: opacity 0.12s ease;
  }
  /* desktop (hover-capable): keep the row visually quiet by fading the
     edit/delete icons until the card is hovered or an action is
     focused via keyboard. touch devices skip this rule (no
     `hover: hover`) so the icons stay visible — there's no hover
     state to expose them on tap-first input. */
  @media (hover: hover) and (pointer: fine) {
    .std-comment-thread-actions { opacity: 0; }
    .std-comment-thread-item:hover .std-comment-thread-actions,
    .std-comment-thread-item:focus-within .std-comment-thread-actions {
      opacity: 1;
    }
  }
  .std-comment-icon-btn {
    border: none;
    background: transparent;
    color: #8a8f94;
    padding: 4px 6px;
    border-radius: 4px;
    font-size: 13px;
    line-height: 1;
    cursor: pointer;
    transition: background-color 0.12s ease, color 0.12s ease;
  }
  .std-comment-icon-btn:hover,
  .std-comment-icon-btn:focus {
    background: #eef2f6;
    color: #1f4a73;
    outline: none;
  }
  .std-comment-icon-btn--danger:hover,
  .std-comment-icon-btn--danger:focus {
    background: #fde7e9;
    color: #c82333;
  }
  .std-comment-thread-body {
    margin-top: 8px;
    /* align body's left edge with the name + timestamp (the meta column).
       40px avatar + 10px head gap = 50px to clear both. previous 42px
       was avatar-width + 2px and left the body shifted ~8px left of
       where the name begins. */
    margin-left: 50px;
  }
  .std-comment-thread-text {
    font-size: 14px;
    line-height: 1.5;
    color: #333;
    white-space: pre-wrap;
    word-break: break-word;
  }
  .std-comment-thread-text a {
    color: #1f4a73;
    text-decoration: underline;
  }
  /* collapsed-state cap for long description / internal notes / comment
     bodies. content is clipped at 200px and the last ~30px fades out so
     the nearby "Show more" link reads as "there is more below, click to
     reveal it". we intentionally only apply this class when the body
     actually overflows (see `*Overflow` booleans in the template bindings)
     — if the content fits, no clip, no fade, no link. the old scroll-box
     variant was awkward on desktop (users scroll the page, not the card)
     and useless on mobile (the touch gesture always scrolls the outer
     page first), so the "Show more" link is the ONLY way to expand. the
     fade is deliberately short: a long fade reads as a gratuitous effect,
     a short one reads as "the text is trailing off, there's an affordance
     right below it". */
  .std-collapsible--collapsed {
    max-height: 200px;
    overflow: hidden;
    -webkit-mask-image: linear-gradient(to bottom, black 85%, transparent 100%);
            mask-image: linear-gradient(to bottom, black 85%, transparent 100%);
  }
  /* "Show more" / "Show less" — kept as a <button> for semantics
     (disclosure control, tab-focusable) but styled as a compact text
     link so it doesn't visually compete with primary actions. sits
     immediately under the faded edge so the fade -> link hand-off reads
     as one gesture. */
  .std-expand-btn {
    display: inline-block;
    margin-top: 2px;
    padding: 0;
    background: none;
    border: none;
    /* FI-1871: kept literal — a minor show-more link, not a structural rail;
       theming it to the rail token would visibly shift it in the original theme. */
    color: #1a7abf;
    font-size: 12px;
    line-height: 1.2;
    cursor: pointer;
    user-select: none;
  }
  .std-expand-btn:hover,
  .std-expand-btn:focus {
    text-decoration: underline;
    outline: none;
  }
  .std-comment-thread-attachments {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 10px;
    margin-left: 42px;
  }
  .std-comment-thread-attachment {
    display: inline-flex;
    align-items: center;
    text-decoration: none;
    color: inherit;
    border: 1px solid #dee2e6;
    border-radius: 6px;
    padding: 4px;
    background: #fff;
    max-width: 220px;
  }
  .std-comment-thread-thumb {
    max-height: 120px;
    max-width: 200px;
    border-radius: 4px;
    object-fit: contain;
  }
  .std-comment-thread-file {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    padding: 4px 6px;
    min-width: 0;
  }
  .std-comment-thread-file-icon { font-size: 16px; }
  .std-comment-thread-file-name {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  /* narrow viewports: drop the body indent so wrapped text doesn't
     kneel against a thin column on the right. */
  @media (max-width: 560px) {
    .std-comment-thread-body,
    .std-comment-thread-attachments {
      margin-left: 0;
    }
  }

  /* empty state — mirrors nl-state-empty from notifications_list.vue:
     centered column with a muted icon, a bolder title line, and a
     softer sub-copy line. keeps the two surfaces reading as the same
     system. */
  .std-comment-empty {
    text-align: center;
    padding: 36px 20px 28px;
    color: #6c757d;
  }
  .std-comment-empty .fa {
    display: block;
    font-size: 32px;
    margin-bottom: 8px;
    color: #cdd6e0;
  }
  .std-comment-empty-title {
    font-size: 14px;
    font-weight: 600;
    color: #2d3748;
    margin-bottom: 2px;
  }
  .std-comment-empty-sub { font-size: 12px; }

  /* composer button icons — tighten the fa gap so the icon reads as
     part of the label, not a separate glyph. */
  .std-comment-composer-row .btn .fa { margin-right: 4px; }
  .std-comment-post .fa { margin-right: 4px; }

  #comments_table td {
    vertical-align: top !important;
    text-align: left !important;
    padding: 0.5rem !important;
  }
  .edited-time {
    font-size: .75rem;
    color: #666666 !important;
  }
  .attachment-grid {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }
  .attachment-item a {
    display: block;
    text-decoration: none;
  }
  .attachment-item {
    position: relative;
    width: 130px;
  }
  .attachment-thumbnail {
    width: 100%;
    height: 80px;
    border-radius: 4px;
    object-fit: contain;
  }
  .existing-attachment,
  .queued-attachment {
    padding: 8px;
    border: 1px solid #dee2e6;
    border-radius: 6px;
    background: #fff;
    overflow: hidden;
  }
  .attachment-selected {
    border-color: var(--fi-action-primary);
    box-shadow: 0 0 0 2px var(--fi-action-primary-ring);
  }
  .attachment-selector {
    position: absolute;
    top: 0;
    right: 0;
    z-index: 2;
    margin: 0;
    padding: 12px;
    cursor: pointer;
    display: flex;
    align-items: flex-start;
    justify-content: flex-end;
  }
  .attachment-file {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 4px;
    min-width: 80px;
    height: 80px;
    justify-content: center;
  }
  .attachment-filename {
    font-size: 0.65rem;
    color: #495057;
    word-break: break-all;
    text-align: center;
    max-width: 110px;
  }
  .attachment-meta {
    margin-top: 6px;
    text-align: center;
  }
  .attachment-name {
    font-size: 0.7rem;
    color: #343a40;
    word-break: break-word;
  }
  .attachment-status {
    font-size: 0.65rem;
    color: #6c757d;
    margin-top: 2px;
  }
  .attachment-error {
    font-size: 0.65rem;
    color: #c82333;
    margin-top: 2px;
  }
  .attachment-remove {
    position: absolute;
    top: 4px;
    right: 4px;
    border: none;
    background: rgba(0, 0, 0, 0.7);
    color: white;
    border-radius: 999px;
    width: 20px;
    height: 20px;
    line-height: 1;
    font-size: 0.8rem;
    cursor: pointer;
  }
  .attachment-state-uploading {
    border-color: var(--fi-action-primary);
  }
  .attachment-state-uploaded {
    border-color: #28a745;
  }
  .attachment-state-failed {
    border-color: #dc3545;
  }
  .comment-attachments {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 4px;
  }
  .comment-attachment-thumbnail {
    max-height: 150px;
    max-width: 250px;
    border-radius: 4px;
    border: 1px solid #dee2e6;
    object-fit: contain;
  }
  .attachment-thumbnail--clickable,
  .std-comment-thread-thumb--clickable {
    cursor: pointer;
  }
  .attachment-thumbnail--clickable:hover,
  .std-comment-thread-thumb--clickable:hover {
    opacity: 0.85;
  }
</style>

<style>
  .std-lightbox-body {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 8px;
  }
  .std-lightbox-img {
    max-width: 100%;
    max-height: 80vh;
    object-fit: contain;
    border-radius: 4px;
  }
</style>